import React, { useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  Box,
  IconButton,
  Popover,
  Typography,
  Snackbar,
  Alert,
  Tooltip,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from "@mui/material";
import { Send, Mic, Image as ImageIcon, AddShoppingCart, PlaceRounded, Close } from "@mui/icons-material";
import { isWhatsappAcceptedImageUrl } from "../../utils/whatsappProductImage";
import { useUsers } from "../../hooks/useUsers";
import { useAgents } from "../../hooks/useAgents";
import MicRecorder from "mic-recorder-to-mp3";
import ChannelSelector, {
  INTERNAL_NOTE_CHANNEL_ID,
  PERSONAL_WHATSAPP_CHANNEL_ID,
} from "./ChannelSelector";
import ComposeModeBar, { COMPOSE_MODES } from "./ComposeModeBar";
import MessageInputField from "./MessageInputField";
import FileDropHandler from "./FileDropHandler";
import FilePreview from "./FilePreview";
import AudioPreview from "./AudioPreview";
import RecordingIndicator from "./RecordingIndicator";
import EmojiPicker from "./EmojiPicker";
import { composerHasTextPayload, composerPlainText } from "./composerHtmlUtils";
import { emailAddressListError, emailAttachmentLimitError } from "./emailCompose";
import {
  COMPOSER_MOBILE_ROW_HEIGHT_PX,
  COMPOSER_ROW_HEIGHT_PX,
  composerPrimaryActionButtonSx,
} from "./composerConstants";
import { useMediaQuery } from "@mui/material";
import { getChannelTypeById, getChannelById } from "../../utils/utils";
import { useChannels } from "../../hooks/useChannels";
import MessagePhoneChannel from "./MessagePhoneChannel";
import {
  getMetaMessagingWindowState,
  getMetaChannelLabel,
  isMetaMessagingWindowChannel,
  resolveMessagingWindowLastInboundDate,
  channelTypeToMessagingWindowFamily,
} from "../../utils/metaMessagingWindowUtils";
import { getWhatsAppWindowState } from "../../utils/whatsappWindowUtils";
import MessageWhatsAppChannel from "./MessageWhatsAppChannel";
import MessageEmailChannel from "./MessageEmailChannel";
import MessageMessengerChannel from "./MessageMessengerChannel";
import MessageSMSChannel from "./MessageSMSChannel";
import MessageAppChannel from "./MessageAppChannel";
import MessageWordpressChannel from "./MessageWordpressChannel";
import { composeChannelFormatOptions } from "./composeFormatOptions";
import {
  generateSuggestedReply,
  getConversationMessageSuggestions,
  fetchData,
  putConversationCart,
  createVideoRoom,
  getVideoRoomByLead,
  createGoogleMeetLink,
  getGoogleCalendarOauthUrl,
  handoffVideoRoom,
  getApifacilGroupParticipants,
} from "../../api/client";
import { useProducts } from "../../hooks/useProducts";
import { isWhatsAppQrChannelTipo, isWhatsAppOfficialChannelTipo, CHANNEL_TYPES, isActiveChannel, isComposerOutboundChannelTipo, isComposerOutboundChannel, normalizeComposerChannelType } from "../../constants/channelCatalog";
import { whatsappQrCapabilities } from "../../utils/whatsappQrCapabilities";
import { useQueryClient } from "react-query";
import { workspaceScopedQueryKey } from "../../utils/workspaceQueryScope";
import { isLikelyWhatsappGroupJid } from "../../utils/whatsappJid";
import {
  composerHasWhatsappGroupMention,
  groupMentionPickerOptions,
  isWhatsappMentionOption,
  mentionDisplayName,
  mentionsForOutboundPayload,
  mergeWhatsappGroupParticipants,
  participantsFromGroupMessages,
  resolveWhatsappGroupId,
  isWhatsappGroupConversation,
  sendableWhatsappMentionToken,
  looksLikeWhatsappGroupUserId,
  participantMentionToken,
  excludeWhatsappGroupSelf,
  isUsableWhatsappParticipantName,
  selfIdentitiesFromGroupMessages,
} from "../../utils/whatsappQrGroupMentions";
import { useTranslation } from "react-i18next";
import StartVideoCallDialog from "./StartVideoCallDialog";
import { Link as RouterLink } from "react-router-dom";
import {
  readWorkspaceLocal,
  readWorkspaceSessionDynamic,
  writeWorkspaceLocal,
  writeWorkspaceSessionDynamic,
  WS_KEY,
} from "../../utils/workspaceStorage";
import {
  channelTypeRequiresClientPhone,
  hasClientPhoneRegistered,
  shouldSkipClientPhoneForWhatsappGroupSend,
  canUseClientChannelForConversation,
} from "../../utils/clientContactUtils";
import {
  resolveDefaultReplyChannel,
  resolveClientComposeChannelRestore,
  shouldDefaultToInternalNoteForTransferHandoff,
} from "../../utils/composeDefaultUtils";
import {
  channelHasWebchatRedirect,
  defaultWebchatTransport,
  resolveWebchatPresence,
  canSendViaWebchat,
  mergePresenceWithRecentWebchatMessages,
} from "../../utils/webchatTransport";
import WebchatTransportBar from "./WebchatTransportBar";
import { deriveConversationContext } from "../../utils/conversationContextUtils";
import { buildStaticMapPreviewUrl } from "../../utils/locationMapPreview";
import { buildCustomerDialDestinations } from "../../utils/phone";
import ShareContactDialog from "./ShareContactDialog";
import { useAuthContext } from "../../context/AuthContext";
import { useEffectivePermissions } from "../../hooks/useEffectivePermissions";
import {
  filterChannelsNotBlocked,
  isOutboundChannelIdBlocked,
} from "../../utils/cargoChannelSendBlocks";
import { useSocketContext } from "../../context/SocketContext";
import { playWaitBeep } from "../../pages/PublicVideo/videoDevices";
import { openAgentVideoJoinUrl } from "../../utils/activeVideoUi";
import {
  CHAT_DRAFT_MODES,
  clearChatMessageDraft,
  loadChatComposerDraft,
  resolveTransferToFromMentionText,
  saveChatMessageDraft,
} from "../../pages/Chat/utils/chatMessageDrafts";

const formatOptions = composeChannelFormatOptions;

const EXTERNAL_CLIENT_BANNER_DONE_KEY = (conversationId) =>
  conversationId != null ? `xpro_extClientBannerDone_${conversationId}` : null;

function mentionHintKey(baseKey, userId) {
  const id = Number(userId);
  return Number.isFinite(id) && id > 0 ? `${baseKey}:${id}` : String(baseKey || "");
}

function hasSeenMentionHint(baseKey, userId) {
  try {
    const scoped = mentionHintKey(baseKey, userId);
    if (readWorkspaceLocal(scoped) === "1") return true;
    if (scoped !== baseKey && readWorkspaceLocal(baseKey) === "1") {
      writeWorkspaceLocal(scoped, "1");
      return true;
    }
  } catch (_) {
    /* ignore */
  }
  return false;
}

function markMentionHintSeen(baseKey, userId) {
  try {
    writeWorkspaceLocal(mentionHintKey(baseKey, userId), "1");
  } catch (_) {
    /* ignore */
  }
}

/** Tipos sem envio ao cliente — não exibir aviso de envio externo. */
function isClientOutboundChannelTipo(tipo) {
  return isComposerOutboundChannelTipo(tipo);
}

function hasMyExternalOutboundMessage(messagesList, myUserId) {
  if (myUserId == null) return false;
  const me = Number(myUserId);
  if (!Number.isFinite(me)) return false;
  const list = Array.isArray(messagesList) ? messagesList : [];
  return list.some((m) => {
    const typ = String(m?.type || "").toLowerCase();
    if (typ === "note" || typ === "log" || typ === "xpro") return false;
    const st = String(m?.senderType || m?.sender || "").toLowerCase();
    if (!["agent", "operator", "user", "chatbot", "ai_agent"].includes(st)) return false;
    const uid = Number(m?.userId ?? m?.id_usuario ?? m?.senderUserId ?? 0);
    return uid === me && uid !== 0;
  });
}

const isChatBotUser = (u) => Number(u?.ura) === 1;

function catalogCoverUrl(product) {
  const g = product?.galeria;
  if (!Array.isArray(g) || !g.length) return null;
  const c = g.find((x) => x?.isCover) || g[0];
  const u = c?.url || c?.src;
  return u && String(u).trim() ? String(u).trim() : null;
}

function MentionPickerRow({ opt, highlighted, onSelect, compact = false }) {
  const whatsapp = isWhatsappMentionOption(opt);
  return (
    <Box
      component="div"
      onClick={() => onSelect(opt)}
      onMouseDown={(e) => e.preventDefault()}
      sx={{
        px: 1.5,
        py: compact ? 1 : 0.9,
        cursor: "pointer",
        bgcolor: highlighted ? "action.selected" : "transparent",
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      {compact ? (
        <>
          <Typography variant="body2" component="span">
            {String(opt.label ?? "")}
          </Typography>
          {opt.kind ? (
            <Typography variant="caption" color="text.secondary" component="span" sx={{ ml: 1 }}>
              ({String(opt.kind)})
            </Typography>
          ) : null}
        </>
      ) : (
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap>
              {String(opt.label ?? "")}
            </Typography>
            {opt.detail ? (
              <Typography variant="caption" color="text.secondary" display="block" noWrap>
                {String(opt.detail)}
              </Typography>
            ) : null}
          </Box>
          {opt.actionLabel ? (
            <Chip
              size="small"
              label={String(opt.actionLabel)}
              color={whatsapp ? "success" : "default"}
              variant={whatsapp ? "filled" : "outlined"}
              sx={{
                height: 22,
                fontSize: "0.68rem",
                fontWeight: 600,
                flexShrink: 0,
                mt: 0.1,
              }}
            />
          ) : opt.kind ? (
            <Typography variant="caption" color="text.secondary" component="span">
              ({String(opt.kind)})
            </Typography>
          ) : null}
        </Box>
      )}
    </Box>
  );
}

const resolveDefaultReplyChannelId = (messagesList, conversation, channels) =>
  resolveDefaultReplyChannel(messagesList, conversation, channels) ??
  INTERNAL_NOTE_CHANNEL_ID;

function pickJsonWhatsappId(raw) {
  if (!raw) return "";
  let obj = raw;
  if (typeof raw === "string") {
    try {
      obj = JSON.parse(raw);
    } catch {
      return "";
    }
  }
  if (!obj || typeof obj !== "object") return "";
  const candidates = [
    obj?.data?.messageId,
    obj?.data?.message_id,
    obj?.messageId,
    obj?.message_id,
    obj?.xpro_last_status_event?.payload?.message_id,
    obj?.xpro_last_status_event?.payload?.messageId,
  ];
  for (const item of candidates) {
    const value = item != null ? String(item).trim() : "";
    if (value && !/^\d+$/.test(value) && !/^msg_/i.test(value)) return value;
  }
  return "";
}

function quoteWhatsappIdFromMessage(message) {
  if (!message || typeof message !== "object") return "";
  const stored = String(message.id_msg_whatsapp || message.idMsgWhatsapp || "").trim();
  const fromJson = pickJsonWhatsappId(message.json_whatsapp || message.jsonWhatsapp);
  const storedIsWeak = !stored || /^\d+$/.test(stored) || /^msg_/i.test(stored);
  if (fromJson && storedIsWeak) return fromJson;
  return stored || fromJson;
}

function quoteParticipantFromMessage(message) {
  if (!message || typeof message !== "object") return "";
  return String(
    message.participant?.jid ||
      message.participant?.phone ||
      message.group?.participantJid ||
      ""
  ).trim();
}

const MessageInput = ({
  onSendMessage,
  selectedConversation,
  messages: messagesIn = [],
  hasHistory = false,
  onRefetchConversationDetails,
  /** Notifica o canal efetivo do seletor (envio) — ex.: barra de voz deve seguir este id, não só o da solicitação. */
  onComposeChannelChange,
  quoteDraft = null,
  onClearQuote,
}) => {
  const messages = Array.isArray(messagesIn) ? messagesIn : [];
  const { userData } = useAuthContext();
  const { socket } = useSocketContext();
  const { data: permData } = useEffectivePermissions();
  const { data: channelsData } = useChannels();
  const channels = channelsData?.channels;
  const sendableChannels = useMemo(
    () =>
      filterChannelsNotBlocked(channels, permData?.blockedOutboundChannelIds, {
        isMaster: Number(userData?.master) === 1,
      }),
    [channels, permData?.blockedOutboundChannelIds, userData?.master]
  );
  const queryClient = useQueryClient();
  const { t } = useTranslation("chat");
  const [phoneBlockSnackbarOpen, setPhoneBlockSnackbarOpen] = useState(false);
  const [webchatTransportOverride, setWebchatTransportOverride] = useState(null);
  const webchatPresenceFollowedRef = useRef(null);
  const [selectedChannel, setSelectedChannel] = useState(() => {
    if (selectedConversation?.kind === "personal") return PERSONAL_WHATSAPP_CHANNEL_ID;
    return (
      readWorkspaceSessionDynamic(`selectedChannel${selectedConversation?.id}`) ||
      selectedConversation?.channelId
    );
  });
  const [composeMode, setComposeMode] = useState(() => {
    if (selectedConversation?.kind === "personal") return COMPOSE_MODES.CLIENT;
    const initialChannel =
      readWorkspaceSessionDynamic(`selectedChannel${selectedConversation?.id}`) ||
      selectedConversation?.channelId;
    return String(initialChannel) === String(INTERNAL_NOTE_CHANNEL_ID)
      ? COMPOSE_MODES.INTERNAL
      : COMPOSE_MODES.CLIENT;
  });
  const [message, setMessage] = useState("");
  const messageRaw = typeof message === "string" ? message : "";
  const messagePlainText = composerPlainText(messageRaw);
  const [files, setFiles] = useState([]);
  const [emailSendError, setEmailSendError] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const emailSendingRef = useRef(false);
  const [audioURL, setAudioURL] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const mp3RecorderRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const discardRequestedRef = useRef(false);
  const [emojiAnchorEl, setEmojiAnchorEl] = useState(null);
  const fileInputRef = useRef(null);
  const [origin, setOrigin] = useState("computer");
  const [destination, setDestination] = useState("");
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [isExpanded, setIsExpanded] = useState(false);
  const [isGeneratingSuggestion, setIsGeneratingSuggestion] = useState(false);
  const [suggestions, setSuggestions] = useState({ templates: [], quickReplies: [] });
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [suggestionsError, setSuggestionsError] = useState(null);
  const [transferTo, setTransferTo] = useState(null);
  const [whatsappApiMembers, setWhatsappApiMembers] = useState([]);
  const [whatsappSelf, setWhatsappSelf] = useState(null);
  const [whatsappMentions, setWhatsappMentions] = useState([]);
  const [whatsappMentionNames, setWhatsappMentionNames] = useState({});
  const [whatsappMentionAll, setWhatsappMentionAll] = useState(false);
  const [pollDraft, setPollDraft] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const mentionAnchorRef = useRef(null);
  const suggestionExtrasRef = useRef({ focusProductIds: [], focusLineItemIds: [] });
  const [mentionHighlightIndex, setMentionHighlightIndex] = useState(0);
  const [productHighlightIndex, setProductHighlightIndex] = useState(0);
  const [mentionOverlayPosition, setMentionOverlayPosition] = useState(null);
  const [cartSaving, setCartSaving] = useState(false);
  const [locationSending, setLocationSending] = useState(false);
  const [locationConfirmOpen, setLocationConfirmOpen] = useState(false);
  const [locationDraft, setLocationDraft] = useState(null);
  const [locationConfirmMapFailed, setLocationConfirmMapFailed] = useState(false);
  const [shareContactOpen, setShareContactOpen] = useState(false);
  const [videoSending, setVideoSending] = useState(false);
  const [joinVideoSending, setJoinVideoSending] = useState(false);
  const [activeVideoRoom, setActiveVideoRoom] = useState(null);
  const [googleMeetSending, setGoogleMeetSending] = useState(false);
  const [videoGuide, setVideoGuide] = useState(null);
  const [startVideoDialogOpen, setStartVideoDialogOpen] = useState(false);
  const lastRequireCameraRef = useRef(false);
  /** Quando o convite vai pelo composer, anexa o botão WhatsApp no próximo envio. */
  const [pendingVideoInviteButton, setPendingVideoInviteButton] = useState(null);
  const [calendarConnecting, setCalendarConnecting] = useState(false);
  const [composeRuleSnackbarOpen, setComposeRuleSnackbarOpen] = useState(false);
  const [composeRuleKey, setComposeRuleKey] = useState(null);
  const [mentionConfirmOpen, setMentionConfirmOpen] = useState(false);
  const [mentionConfirmKind, setMentionConfirmKind] = useState("colleague");
  const [guestWaitingAlert, setGuestWaitingAlert] = useState(null);
  const [handoffAlert, setHandoffAlert] = useState(null);
  const [handoffTargetUserId, setHandoffTargetUserId] = useState("");
  const [handoffSending, setHandoffSending] = useState(false);
  const [videoReuseSnackbarOpen, setVideoReuseSnackbarOpen] = useState(false);
  const lastClientChannelRef = useRef(null);
  const messageRef = useRef("");
  /** null no 1º mount para forçar carga do rascunho do modo atual. */
  const conversationIdRef = useRef(null);
  const draftModeRef = useRef(null);
  const transferToRef = useRef(null);
  const selectedTemplateRef = useRef(null);
  const pendingVideoInviteButtonRef = useRef(null);
  const draftContextRef = useRef({ userId: userData?.id });
  /** Evita reaplicar default a cada tick de `messages` (mantém 1 pass de handoff). */
  const channelBootstrapRef = useRef({ convId: null, fromSession: false, autoKey: null });

  const draftContext = useMemo(
    () => ({ userId: userData?.id }),
    [userData?.id]
  );

  const draftModeForCompose = useCallback(
    (mode) =>
      mode === COMPOSE_MODES.INTERNAL ? CHAT_DRAFT_MODES.INTERNAL : CHAT_DRAFT_MODES.CLIENT,
    []
  );

  const persistMessageDraft = useCallback(
    (convId, rawText, mode = draftModeRef.current || COMPOSE_MODES.CLIENT, extras = {}) => {
      if (!convId) return;
      const raw = typeof rawText === "string" ? rawText : "";
      const toStore = composerPlainText(raw).trim() ? raw : "";
      saveChatMessageDraft(convId, toStore, {
        ...draftContextRef.current,
        mode: draftModeForCompose(mode),
        transferTo:
          extras.transferTo !== undefined ? extras.transferTo : transferToRef.current,
        selectedTemplate:
          extras.selectedTemplate !== undefined
            ? extras.selectedTemplate
            : selectedTemplateRef.current,
        pendingVideoInviteButton:
          extras.pendingVideoInviteButton !== undefined
            ? extras.pendingVideoInviteButton
            : pendingVideoInviteButtonRef.current,
      });
    },
    [draftModeForCompose]
  );

  useEffect(() => {
    messageRef.current = messageRaw;
  }, [messageRaw]);

  useEffect(() => {
    transferToRef.current = transferTo;
  }, [transferTo]);

  useEffect(() => {
    selectedTemplateRef.current = selectedTemplate;
  }, [selectedTemplate]);

  useEffect(() => {
    pendingVideoInviteButtonRef.current = pendingVideoInviteButton;
  }, [pendingVideoInviteButton]);

  useEffect(() => {
    draftContextRef.current = draftContext;
  }, [draftContext]);

  useEffect(() => {
    const nextConvId = selectedConversation?.id ?? null;
    const prevConvId = conversationIdRef.current;
    const prevMode = draftModeRef.current;
    const nextMode = composeMode;

    if (prevConvId != null && prevConvId !== nextConvId) {
      persistMessageDraft(prevConvId, messageRef.current, prevMode || COMPOSE_MODES.CLIENT);
    } else if (
      prevConvId != null &&
      prevConvId === nextConvId &&
      prevMode != null &&
      prevMode !== nextMode
    ) {
      persistMessageDraft(prevConvId, messageRef.current, prevMode);
    }

    if (nextConvId) {
      const shouldReload =
        prevConvId !== nextConvId || prevMode == null || prevMode !== nextMode;
      if (shouldReload) {
        const draft = loadChatComposerDraft(nextConvId, {
          ...draftContext,
          mode: draftModeForCompose(nextMode),
        });
        setMessage(draft.text);
        setTransferTo(draft.transferTo);
        setSelectedTemplate(draft.selectedTemplate);
        setPendingVideoInviteButton(draft.pendingVideoInviteButton);
      }
    } else if (prevConvId != null) {
      setMessage("");
      setTransferTo(null);
      setSelectedTemplate(null);
      setPendingVideoInviteButton(null);
    }

    conversationIdRef.current = nextConvId;
    draftModeRef.current = nextMode;
  }, [
    selectedConversation?.id,
    composeMode,
    draftContext,
    draftModeForCompose,
    persistMessageDraft,
  ]);

  useEffect(() => {
    const convId = selectedConversation?.id;
    if (!convId) return undefined;
    const timer = setTimeout(() => {
      if (conversationIdRef.current === convId) {
        persistMessageDraft(convId, messageRef.current, draftModeRef.current);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [
    selectedConversation?.id,
    messageRaw,
    transferTo,
    selectedTemplate,
    pendingVideoInviteButton,
    persistMessageDraft,
  ]);

  useEffect(
    () => () => {
      const convId = conversationIdRef.current;
      if (convId) {
        persistMessageDraft(convId, messageRef.current, draftModeRef.current);
      }
    },
    [persistMessageDraft]
  );

  useEffect(() => {
    setPendingVideoInviteButton(null);
  }, [selectedConversation?.id]);

  useEffect(() => {
    const handler = (ev) => {
      const d = ev.detail || {};
      if (d.conversationId != null && Number(d.conversationId) !== Number(selectedConversation?.id)) {
        return;
      }
      if (d.setText != null) {
        setMessage(String(d.setText));
      } else if (d.appendText) {
        const t = String(d.appendText);
        setMessage((m) => (m && m.trim() ? `${m.trim()}\n\n${t}` : t));
      }
      if (d.buttonMessage) {
        setPendingVideoInviteButton({
          buttonMessage: String(d.buttonMessage),
          joinUrl: d.joinUrl ? String(d.joinUrl) : null,
        });
      }
      if (d.imageUrl && /^https?:\/\//i.test(String(d.imageUrl))) {
        const url = String(d.imageUrl);
        (async () => {
          const blobToFile = (blob) => {
            const ext =
              (blob.type || "").includes("png") ? "png" : (blob.type || "").includes("webp") ? "webp" : "jpeg";
            return new File([blob], `imagem.${ext}`, { type: blob.type || "image/jpeg" });
          };
          try {
            let blob;
            try {
              const r = await fetch(url, { mode: "cors" });
              if (!r.ok) throw new Error(`HTTP ${r.status}`);
              blob = await r.blob();
            } catch {
              // Loja/CDN sem CORS: o backend já baixa e envia ao S3 (mesmo fluxo da importação de produtos).
              const resp = await fetchData("/uploads/fetch", "POST", {
                url,
                prefix: "chat-compose",
                filename: "imagem",
              });
              const proxied = resp?.data?.url;
              if (!proxied) throw new Error("proxy sem url");
              const r2 = await fetch(proxied, { mode: "cors" });
              if (!r2.ok) throw new Error(`S3 ${r2.status}`);
              blob = await r2.blob();
            }
            setFiles([blobToFile(blob)]);
            setAudioURL(null);
          } catch {
            // eslint-disable-next-line no-alert
            alert("Não foi possível baixar a imagem (CORS). O link foi colocado no texto.");
            setMessage((m) => (m && m.trim() ? `${m.trim()}\n${url}` : url));
          }
        })();
      }
      if (Array.isArray(d.focusProductIds) && d.focusProductIds.length) {
        suggestionExtrasRef.current.focusProductIds = d.focusProductIds
          .map((x) => Number(x))
          .filter((n) => n > 0);
      }
      if (Array.isArray(d.focusLineItemIds) && d.focusLineItemIds.length) {
        suggestionExtrasRef.current.focusLineItemIds = d.focusLineItemIds
          .map((x) => Number(x))
          .filter((n) => n > 0);
      }
    };
    window.addEventListener("xpro-chat-compose", handler);
    return () => window.removeEventListener("xpro-chat-compose", handler);
  }, [selectedConversation?.id]);

  // Precisa incluir ChatBots (ura=1) para o @/transferência permitir direcionar ao bot.
  const { data: usersResp } = useUsers(1, 1000, "", "todos", { order: "nome" });
  const { data: agentsResp } = useAgents(1, 200, "");
  const users = usersResp?.users ?? [];
  const agents = agentsResp?.agents ?? [];
  const humanUsers = (users || []).filter((u) => !isChatBotUser(u));
  const chatbotUsers = (users || []).filter(isChatBotUser);

  const lastAt = messagePlainText.lastIndexOf("@");
  const lastHash = messagePlainText.lastIndexOf("#");
  const mentionQuery = lastAt >= 0 ? messagePlainText.slice(lastAt + 1) : "";
  const productQuery = lastHash >= 0 ? messagePlainText.slice(lastHash + 1) : "";
  const atMentionToken = lastAt >= 0 && !/\s/.test(mentionQuery);
  const hashProductToken = lastHash >= 0 && !/\s/.test(productQuery);
  /** Qual autocompletar vale: o gatilho mais à direita na mensagem ganha (igual fluxo @ vs #). */
  const mentionPickerActive = atMentionToken && (!hashProductToken || lastAt > lastHash);
  const productPickerActive = hashProductToken && (!atMentionToken || lastHash > lastAt);

  const mentionQueryLower = (mentionQuery || "").toLowerCase();
  const productQueryLower = (productQuery || "").toLowerCase();

  const cartProducts = useMemo(() => {
    const raw = selectedConversation?.products;
    if (!Array.isArray(raw)) return [];
    return raw
      .map((p) => ({
        id: p.id,
        productId: p.productId ?? p.product_id ?? null,
        nome: p.nome ?? p.name ?? "",
        quantidade: p.quantidade ?? p.quantity ?? 1,
        valor: p.valor ?? p.value ?? 0,
        coverImageUrl: p.coverImageUrl ?? null,
        sku: p.sku != null ? String(p.sku) : null,
      }))
      .filter((p) => p.nome);
  }, [selectedConversation?.id, selectedConversation?.products]);

  const projectId = selectedConversation?.projectId;
  const productParams = projectId ? { ids_projetos: String(projectId) } : {};
  const catalogEnabled = productPickerActive && Boolean(projectId);
  const { data: catalogData, isLoading: catalogLoading } = useProducts(
    1,
    500,
    productQuery,
    productParams,
    { enabled: catalogEnabled }
  );

  const productOptions = useMemo(() => {
    const matchText = (nome, sku) => {
      if (!productQueryLower) return true;
      const n = (nome || "").toLowerCase();
      const s = sku ? String(sku).toLowerCase() : "";
      return n.includes(productQueryLower) || s.includes(productQueryLower);
    };

    const cartFiltered = cartProducts.filter((line) => matchText(line.nome, line.sku));
    const cartProductIds = new Set(
      cartFiltered.map((c) => c.productId).filter((id) => id != null && !Number.isNaN(Number(id)))
    );

    const catalogList = catalogEnabled ? catalogData?.products ?? [] : [];
    const catalogExtra = catalogList
      .filter((cat) => matchText(cat.nome, cat.sku))
      .filter((cat) => !cartProductIds.has(cat.id))
      .map((cat) => ({
        rowKey: `cat-${cat.id}`,
        inCart: false,
        lineId: null,
        productId: cat.id,
        nome: cat.nome || "",
        quantidade: 1,
        valor: cat.preco != null ? Number(cat.preco) : 0,
        coverImageUrl: catalogCoverUrl(cat),
        sku: cat.sku != null ? String(cat.sku) : null,
      }));

    const cartRows = cartFiltered.map((line) => ({
      rowKey: `line-${line.id}`,
      inCart: true,
      lineId: line.id,
      productId: line.productId,
      nome: line.nome,
      quantidade: line.quantidade,
      valor: line.valor,
      coverImageUrl: line.coverImageUrl,
      sku: line.sku,
    }));

    const byName = (a, b) => (a.nome || "").localeCompare(b.nome || "", "pt");
    return [...cartRows.sort(byName), ...catalogExtra.sort(byName)];
  }, [cartProducts, catalogData?.products, catalogEnabled, productQueryLower]);

  const conversationId = selectedConversation?.id;

  const handleAddProductToConversation = useCallback(
    async (row) => {
      if (row.inCart || !row.productId || conversationId == null) return;
      const withPid = cartProducts.filter((p) => p.productId);
      if (withPid.length !== cartProducts.length) {
        // eslint-disable-next-line no-alert
        alert("Cada item do protocolo precisa estar vinculado a um produto do catálogo. Ajuste em Produtos.");
        return;
      }
      setCartSaving(true);
      try {
        const items = withPid.map((p) => ({
          productId: p.productId,
          quantity: p.quantidade,
          unitPrice: p.valor,
        }));
        items.push({
          productId: row.productId,
          quantity: 1,
          unitPrice: row.valor,
        });
        await putConversationCart(conversationId, items);
        if (typeof onRefetchConversationDetails === "function") {
          await onRefetchConversationDetails();
        }
      } catch (e) {
        // eslint-disable-next-line no-alert
        alert(e?.message || "Erro ao vincular produto ao protocolo.");
      } finally {
        setCartSaving(false);
      }
    },
    [cartProducts, conversationId, onRefetchConversationDetails]
  );

  const qrGroupIdForMentions = useMemo(() => {
    const ch = getChannelById(channels, selectedChannel);
    const tipo = ch?.tipo != null && ch?.tipo !== "" ? Number(ch.tipo) : null;
    if (!isWhatsAppQrChannelTipo(tipo)) return null;
    return resolveWhatsappGroupId(selectedConversation, messages);
  }, [channels, selectedChannel, selectedConversation, messages]);

  const whatsappHistoryMembers = useMemo(
    () =>
      qrGroupIdForMentions
        ? participantsFromGroupMessages(messages, {
            groupName: selectedConversation?.name || selectedConversation?.nome || "",
          })
        : [],
    [qrGroupIdForMentions, messages, selectedConversation?.name, selectedConversation?.nome]
  );
  const whatsappHistorySelf = useMemo(
    () => (qrGroupIdForMentions ? selfIdentitiesFromGroupMessages(messages) : []),
    [qrGroupIdForMentions, messages]
  );
  const whatsappGroupMembers = useMemo(
    () =>
      excludeWhatsappGroupSelf(
        mergeWhatsappGroupParticipants(whatsappApiMembers, whatsappHistoryMembers),
        [whatsappSelf, ...whatsappHistorySelf]
      ),
    [whatsappApiMembers, whatsappHistoryMembers, whatsappSelf, whatsappHistorySelf]
  );

  const mentionOptions = useMemo(() => {
    const transferLabel = t("input.mentionActionTransfer");
    const human = humanUsers.map((u) => ({
      type: "user",
      id: u.id,
      label: u.nome || u.name || "",
      kind: "Humano",
      detail: "Humano",
      action: "transfer",
      actionLabel: transferLabel,
    }));
    const chatbots = chatbotUsers.map((u) => ({
      type: "user",
      id: u.id,
      label: u.nome || u.name || "",
      kind: "ChatBot",
      detail: "ChatBot",
      action: "transfer",
      actionLabel: transferLabel,
    }));
    const agentes = (agents || []).map((a) => ({
      type: "agent",
      id: a.id,
      label: a.name || a.nome || "",
      kind: "Agente de IA",
      detail: "Agente de IA",
      action: "transfer",
      actionLabel: transferLabel,
    }));
    const team = [...human, ...chatbots, ...agentes].filter((o) => o.label);
    const wa = [];
    if (qrGroupIdForMentions) {
      wa.push({
        type: "whatsapp_all",
        id: "all",
        label: t("input.mentionWhatsappAll"),
        kind: t("input.mentionKindWhatsappAll"),
        action: "whatsapp",
        actionLabel: t("input.mentionActionWhatsappAll"),
      });
      for (const p of whatsappGroupMembers) {
        if (
          looksLikeWhatsappGroupUserId(p?.phone) ||
          looksLikeWhatsappGroupUserId(p?.lid) ||
          looksLikeWhatsappGroupUserId(p?.jid)
        ) {
          continue;
        }
        const mentionValue = participantMentionToken(p) || sendableWhatsappMentionToken(p?.phone || p?.jid || p?.lid);
        if (!mentionValue || looksLikeWhatsappGroupUserId(mentionValue)) continue;
        const token = mentionValue;
        const label = isUsableWhatsappParticipantName(p.name, p)
          ? mentionDisplayName(p.name)
          : p.phone || mentionValue;
        wa.push({
          type: "whatsapp",
          id: p.jid || mentionValue,
          label,
          kind: t("input.mentionKindWhatsapp"),
          detail: token && String(label) !== token ? token : undefined,
          action: "whatsapp",
          actionLabel: t("input.mentionActionWhatsapp"),
          mentionValue,
          mentionToken: token,
        });
      }
    }
    const all = [...wa, ...team];
    if (!mentionQueryLower) return all;
    return all.filter((o) => {
      const label = String(o.label || "").toLowerCase();
      const token = String(o.mentionToken || "").toLowerCase();
      return label.includes(mentionQueryLower) || token.includes(mentionQueryLower);
    });
  }, [
    humanUsers,
    chatbotUsers,
    agents,
    mentionQueryLower,
    qrGroupIdForMentions,
    whatsappGroupMembers,
    t,
  ]);

  const mentionPickerSections = useMemo(() => {
    if (!qrGroupIdForMentions) return null;
    const grouped = groupMentionPickerOptions(mentionOptions);
    const sections = [];
    if (grouped.whatsapp.length) {
      sections.push({
        id: "whatsapp",
        title: t("input.mentionSectionWhatsapp"),
        hint: t("input.mentionSectionWhatsappHint"),
        options: grouped.whatsapp,
      });
    }
    if (grouped.internal.length) {
      sections.push({
        id: "internal",
        title: t("input.mentionSectionInternal"),
        hint: t("input.mentionSectionInternalHint"),
        options: grouped.internal,
      });
    }
    return sections;
  }, [qrGroupIdForMentions, mentionOptions, t]);

  const whatsappGroupMentionIntent = useMemo(
    () =>
      composerHasWhatsappGroupMention({
        groupId: qrGroupIdForMentions,
        plainText: messagePlainText,
        mentions: whatsappMentions,
        mentionNames: whatsappMentionNames,
        mentionAll: whatsappMentionAll,
      }),
    [
      qrGroupIdForMentions,
      messagePlainText,
      whatsappMentions,
      whatsappMentionNames,
      whatsappMentionAll,
    ]
  );

  /** Mensagem parece menção a colega (@nome) — confirmação antes de enviar ao cliente por canal externo. */
  const colleagueAtMentionIntent = useMemo(() => {
    if (whatsappGroupMentionIntent) return false;
    const raw = messagePlainText.trimStart();
    if (!raw.startsWith("@")) return false;
    const rest = raw.slice(1).trimStart();
    if (!rest) return false;
    const labels = [
      ...humanUsers.map((u) => u.nome || u.name),
      ...chatbotUsers.map((u) => u.nome || u.name),
      ...(agents || []).map((a) => a.name || a.nome),
    ]
      .filter((x) => x && String(x).trim())
      .map((x) => String(x).trim());
    for (const label of labels) {
      const lowLab = label.toLowerCase();
      const lowRest = rest.toLowerCase();
      if (lowRest.startsWith(`${lowLab} `) || lowRest === lowLab) return true;
      const firstWord = rest.split(/\s+/)[0]?.toLowerCase();
      const labFirst = label.split(/\s+/)[0]?.toLowerCase();
      if (firstWord && labFirst && firstWord === labFirst) return true;
    }
    return false;
  }, [whatsappGroupMentionIntent, messagePlainText, humanUsers, chatbotUsers, agents]);

  const formatCartCurrency = (v) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(v || 0));

  const buildCartProductSummary = (p) =>
    `${p.nome}\n${formatCartCurrency(p.valor)} un. · ${p.quantidade} un.\n${p.sku ? `SKU: ${p.sku}\n` : ""}`.trim();

  const handleSelectMention = (option) => {
    if (lastAt < 0) return;
    if (isWhatsappMentionOption(option)) {
      setTransferTo(null);
      if (option.type === "whatsapp_all") {
        setWhatsappMentionAll(true);
        const prefix = messagePlainText.slice(0, lastAt).replace(/\s+$/, "");
        setMessage(prefix ? `${prefix} ` : "");
        return;
      }
      const token = sendableWhatsappMentionToken(option.mentionToken || option.mentionValue);
      if (!token) return;
      const displayName = mentionDisplayName(option.label, token);
      setMessage(`${messagePlainText.slice(0, lastAt + 1)}${displayName} `);
      setWhatsappMentions((prev) => (prev.includes(token) ? prev : [...prev, token]));
      setWhatsappMentionNames((prev) => ({ ...prev, [token]: displayName }));
      return;
    }
    setWhatsappMentions([]);
    setWhatsappMentionAll(false);
    setWhatsappMentionNames({});
    const newMessage = messagePlainText.slice(0, lastAt + 1) + (option.label || "") + " ";
    setMessage(newMessage);
    setTransferTo({ type: option.type, id: option.id, label: option.label });
  };

  const handleSelectProductText = (p) => {
    if (lastHash < 0) return;
    setMessage(messagePlainText.slice(0, lastHash) + buildCartProductSummary(p) + " ");
  };

  const handleSelectProductImage = (p) => {
    if (lastHash < 0 || !p.coverImageUrl || !isWhatsappAcceptedImageUrl(p.coverImageUrl)) return;
    if (selectedConversation?.id == null) return;
    setMessage(messagePlainText.slice(0, lastHash).replace(/\s+$/, ""));
    window.dispatchEvent(
      new CustomEvent("xpro-chat-compose", {
        detail: {
          conversationId: Number(selectedConversation.id),
          imageUrl: p.coverImageUrl,
          appendText: p.nome || "",
        },
      })
    );
  };

  useEffect(() => {
    setMentionHighlightIndex(0);
  }, [mentionOptions.length, mentionQuery]);

  useEffect(() => {
    setProductHighlightIndex(0);
  }, [productOptions.length, productQuery]);

  const pickerOverlayOpen =
    (mentionPickerActive && mentionOptions.length > 0) || productPickerActive;

  // Position overlay above the input (no focus trap)
  const groupMentionPicker = Boolean(qrGroupIdForMentions);
  useLayoutEffect(() => {
    if (pickerOverlayOpen && mentionAnchorRef.current) {
      const rect = mentionAnchorRef.current.getBoundingClientRect();
      const gap = 8;
      const next = {
        bottom: window.innerHeight - rect.top + gap,
        left: rect.left,
        width: Math.max(rect.width, groupMentionPicker ? 380 : 320),
      };
      setMentionOverlayPosition((prev) =>
        prev &&
        prev.bottom === next.bottom &&
        prev.left === next.left &&
        prev.width === next.width
          ? prev
          : next
      );
    } else {
      setMentionOverlayPosition((prev) => (prev == null ? prev : null));
    }
  }, [pickerOverlayOpen, mentionOptions.length, groupMentionPicker]);

  // Teclado: @ (menções) ou # (produtos: protocolo + catálogo) — Enter = texto; Shift+Enter = foto (JPEG/PNG)
  useEffect(() => {
    const mentionMode = mentionPickerActive && mentionOptions.length > 0;
    const productMode = productPickerActive;
    if (!mentionMode && !productMode) return;

    const onKey = (e) => {
      if (mentionMode) {
        if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          if (lastAt >= 0) setMessage((m) => (typeof m === "string" ? m : "").slice(0, lastAt));
          return;
        }
        if (e.key === "Enter") {
          e.preventDefault();
          e.stopPropagation();
          const opt = mentionOptions[mentionHighlightIndex];
          if (opt) handleSelectMention(opt);
          return;
        }
        if (e.key === "ArrowDown") {
          e.preventDefault();
          e.stopPropagation();
          setMentionHighlightIndex((i) => (i + 1) % mentionOptions.length);
          return;
        }
        if (e.key === "ArrowUp") {
          e.preventDefault();
          e.stopPropagation();
          setMentionHighlightIndex((i) => (i - 1 + mentionOptions.length) % mentionOptions.length);
          return;
        }
        return;
      }

      if (productMode) {
        if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          if (lastHash >= 0) setMessage((m) => (typeof m === "string" ? m : "").slice(0, lastHash));
          return;
        }
        if (e.key === "Enter" && e.shiftKey) {
          if (productOptions.length === 0) return;
          const p = productOptions[productHighlightIndex];
          if (p?.coverImageUrl && isWhatsappAcceptedImageUrl(p.coverImageUrl)) {
            e.preventDefault();
            e.stopPropagation();
            handleSelectProductImage(p);
          }
          return;
        }
        if (e.key === "Enter") {
          if (productOptions.length === 0) return;
          e.preventDefault();
          e.stopPropagation();
          const p = productOptions[productHighlightIndex];
          if (p) handleSelectProductText(p);
          return;
        }
        if (e.key === "ArrowDown") {
          if (productOptions.length === 0) return;
          e.preventDefault();
          e.stopPropagation();
          setProductHighlightIndex((i) => (i + 1) % productOptions.length);
          return;
        }
        if (e.key === "ArrowUp") {
          if (productOptions.length === 0) return;
          e.preventDefault();
          e.stopPropagation();
          setProductHighlightIndex((i) => (i - 1 + productOptions.length) % productOptions.length);
          return;
        }
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [
    mentionPickerActive,
    mentionOptions,
    mentionHighlightIndex,
    lastAt,
    productPickerActive,
    productOptions,
    productHighlightIndex,
    lastHash,
    messagePlainText,
    selectedConversation?.id,
  ]);

  const currentChannel = getChannelById(channels, selectedChannel);
  const webchatPresence = useMemo(
    () =>
      mergePresenceWithRecentWebchatMessages(
        resolveWebchatPresence(selectedConversation),
        messages
      ),
    [selectedConversation, messages]
  );
  const showWebchatTransport =
    selectedConversation?.kind !== "personal" &&
    String(selectedChannel) !== String(INTERNAL_NOTE_CHANNEL_ID) &&
    channelHasWebchatRedirect(currentChannel, selectedConversation);
  const webchatTransport = useMemo(() => {
    if (!showWebchatTransport) return null;
    if (webchatTransportOverride) return webchatTransportOverride;
    return defaultWebchatTransport(webchatPresence);
  }, [showWebchatTransport, webchatTransportOverride, webchatPresence]);

  useEffect(() => {
    const convId = selectedConversation?.id != null ? String(selectedConversation.id) : "";
    const key = `${convId}:${webchatPresence.state}`;
    if (webchatPresenceFollowedRef.current == null) {
      webchatPresenceFollowedRef.current = key;
      return;
    }
    if (webchatPresenceFollowedRef.current !== key) {
      webchatPresenceFollowedRef.current = key;
      setWebchatTransportOverride(null);
    }
  }, [selectedConversation?.id, webchatPresence.state]);

  const selectedChannelTipo =
    String(selectedChannel) === String(PERSONAL_WHATSAPP_CHANNEL_ID)
      ? null
      : currentChannel?.tipo != null && currentChannel?.tipo !== ""
        ? Number(currentChannel.tipo)
        : null;
  const channelTypeRaw =
    String(selectedChannel) === String(PERSONAL_WHATSAPP_CHANNEL_ID)
      ? "WhatsApp"
      : String(selectedChannel) === String(INTERNAL_NOTE_CHANNEL_ID)
        ? "Xpro"
        : getChannelTypeById(channels, selectedChannel);
  // API pode rotular Telefone/Vídeo em PT; o switch do composer usa chaves estáveis (Phone, …).
  const channelType = normalizeComposerChannelType(channelTypeRaw, selectedChannelTipo);
  const channelName =
    String(selectedChannel) === String(PERSONAL_WHATSAPP_CHANNEL_ID)
      ? "WhatsApp pessoal"
      : currentChannel?.nome || currentChannel?.name || null;

  // Canais sem envio (DEFAULT, Ads, Vídeo, etc.), desativados ou bloqueados no cargo.
  useEffect(() => {
    const blockedForCargo = isOutboundChannelIdBlocked(
      selectedChannel,
      permData?.blockedOutboundChannelIds,
      { isMaster: Number(userData?.master) === 1 }
    );
    if (selectedChannelTipo == null && !blockedForCargo) return;
    if (
      !blockedForCargo &&
      isComposerOutboundChannel(currentChannel, selectedChannelTipo) &&
      isActiveChannel(currentChannel)
    ) {
      return;
    }
    const fallback = resolveDefaultReplyChannelId(
      messages,
      selectedConversation,
      sendableChannels
    );
    if (fallback != null && String(fallback) !== String(selectedChannel)) {
      setSelectedChannel(fallback);
      if (String(fallback) === String(INTERNAL_NOTE_CHANNEL_ID)) {
        setComposeMode(COMPOSE_MODES.INTERNAL);
      } else {
        setComposeMode(COMPOSE_MODES.CLIENT);
      }
    }
  }, [
    selectedChannelTipo,
    selectedChannel,
    currentChannel,
    messages,
    selectedConversation,
    channels,
    sendableChannels,
    permData?.blockedOutboundChannelIds,
    userData?.master,
  ]);

  const phoneSendBlocked = useMemo(() => {
    if (String(selectedChannel) === String(INTERNAL_NOTE_CHANNEL_ID)) return false;
    if (String(selectedChannel) === String(PERSONAL_WHATSAPP_CHANNEL_ID)) return false;
    if (selectedConversation?.kind === "personal") return false;
    if (
      shouldSkipClientPhoneForWhatsappGroupSend({
        conversation: selectedConversation,
        messages,
        channelTipo: selectedChannelTipo,
        channelType,
      })
    ) {
      return false;
    }
    return (
      channelTypeRequiresClientPhone(channelType) && !hasClientPhoneRegistered(selectedConversation)
    );
  }, [selectedChannel, selectedChannelTipo, channelType, selectedConversation, messages]);

  const qrCaps = useMemo(
    () =>
      isWhatsAppQrChannelTipo(selectedChannelTipo)
        ? whatsappQrCapabilities(currentChannel?.whatsapp_qr_api_version || currentChannel?.whatsappQrApiVersion)
        : null,
    [selectedChannelTipo, currentChannel]
  );

  const locationBlockedWhatsappGroup = useMemo(() => {
    if (!isWhatsAppQrChannelTipo(selectedChannelTipo)) return false;
    if (qrCaps?.groupLocation) return false;
    if (channelType !== "WhatsApp" && channelType !== "App") return false;
    return isLikelyWhatsappGroupJid(selectedConversation?.celular);
  }, [channelType, selectedChannelTipo, selectedConversation?.celular, qrCaps]);

  useEffect(() => {
    setWhatsappMentions([]);
    setWhatsappMentionNames({});
    setWhatsappMentionAll(false);
    setWhatsappApiMembers([]);
    setWhatsappSelf(null);
  }, [selectedConversation?.id]);

  useEffect(() => {
    let cancelled = false;
    if (!qrGroupIdForMentions || !selectedChannel) {
      setWhatsappApiMembers([]);
      setWhatsappSelf(null);
      return undefined;
    }
    (async () => {
      try {
        const res = await getApifacilGroupParticipants(selectedChannel, qrGroupIdForMentions);
        const list = res?.participants || res?.data?.participants || [];
        if (!cancelled) {
          setWhatsappApiMembers(Array.isArray(list) ? list : []);
          setWhatsappSelf(res?.self || res?.data?.self || null);
        }
      } catch (err) {
        console.warn("Falha ao listar membros do grupo WhatsApp:", err?.message || err);
        if (!cancelled) {
          setWhatsappApiMembers([]);
          setWhatsappSelf(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [qrGroupIdForMentions, selectedChannel]);

  useEffect(() => {
    if (selectedConversation?.kind === "personal") {
      setSelectedChannel(PERSONAL_WHATSAPP_CHANNEL_ID);
      setComposeMode(COMPOSE_MODES.CLIENT);
      channelBootstrapRef.current = {
        convId: selectedConversation?.id ?? null,
        fromSession: true,
        autoKey: null,
      };
      return;
    }

    const convId = selectedConversation?.id;
    if (convId == null) return;

    const savedChannel = readWorkspaceSessionDynamic(`selectedChannel${convId}`);
    const savedBlocked = isOutboundChannelIdBlocked(
      savedChannel,
      permData?.blockedOutboundChannelIds,
      { isMaster: Number(userData?.master) === 1 }
    );
    if (savedChannel && !savedBlocked) {
      setSelectedChannel(savedChannel);
      if (String(savedChannel) === String(INTERNAL_NOTE_CHANNEL_ID)) {
        setComposeMode(COMPOSE_MODES.INTERNAL);
        // Sessão em anotação: guarda um canal real para o toggle “Responder cliente”.
        if (!lastClientChannelRef.current) {
          lastClientChannelRef.current =
            resolveClientComposeChannelRestore({
              conversationChannelId: selectedConversation?.channelId,
              defaultChannelId: sendableChannels.length
                ? resolveDefaultReplyChannel(messages, selectedConversation, sendableChannels)
                : null,
            }) ?? null;
        }
      } else {
        setComposeMode(COMPOSE_MODES.CLIENT);
        lastClientChannelRef.current = savedChannel;
      }
      channelBootstrapRef.current = { convId, fromSession: true, autoKey: null };
      return;
    }

    // Sem preferência salva: espera a lista de canais antes de fechar o default.
    if (!channels) return;

    const transferHandoffInternal = shouldDefaultToInternalNoteForTransferHandoff(
      messages,
      selectedConversation,
      userData
    );
    const lastClientChannelId =
      deriveConversationContext(messages).lastClientMessage?.messageChannelId ?? "";
    const autoKey = [
      selectedConversation?.channelId ?? "",
      selectedConversation?.celular ?? selectedConversation?.phone ?? "",
      selectedConversation?.email ?? "",
      lastClientChannelId,
      transferHandoffInternal ? "1" : "0",
      Array.isArray(sendableChannels) ? sendableChannels.map((c) => c.id).join(",") : "",
    ].join("|");

    const boot = channelBootstrapRef.current;
    if (boot.convId === convId && !boot.fromSession && boot.autoKey === autoKey) {
      return;
    }

    const defaultChannel = resolveDefaultReplyChannelId(
      messages,
      selectedConversation,
      sendableChannels
    );
    const applyInternalDefault = (restoreClientChannel) => {
      lastClientChannelRef.current = restoreClientChannel ?? defaultChannel ?? null;
      setSelectedChannel(INTERNAL_NOTE_CHANNEL_ID);
      setComposeMode(COMPOSE_MODES.INTERNAL);
    };

    if (defaultChannel == null || defaultChannel === "") {
      if (transferHandoffInternal) {
        applyInternalDefault(null);
      } else {
        setSelectedChannel(defaultChannel);
        setComposeMode(COMPOSE_MODES.CLIENT);
      }
      channelBootstrapRef.current = { convId, fromSession: false, autoKey };
      return;
    }

    const defaultChannelType = getChannelTypeById(channels, defaultChannel);
    const defaultChannelObj = getChannelById(channels, defaultChannel);
    if (!defaultChannelType) {
      if (transferHandoffInternal) {
        applyInternalDefault(defaultChannel);
      } else {
        setSelectedChannel(defaultChannel);
        setComposeMode(COMPOSE_MODES.CLIENT);
        lastClientChannelRef.current = defaultChannel;
      }
      channelBootstrapRef.current = { convId, fromSession: false, autoKey };
      return;
    }

    const defaultUsable = canUseClientChannelForConversation(
      selectedConversation,
      defaultChannel,
      defaultChannelType,
      defaultChannelObj?.tipo,
      messages
    );

    if (defaultUsable && !transferHandoffInternal) {
      setSelectedChannel(defaultChannel);
      setComposeMode(COMPOSE_MODES.CLIENT);
      lastClientChannelRef.current = defaultChannel;
    } else {
      applyInternalDefault(defaultChannel);
    }
    channelBootstrapRef.current = { convId, fromSession: false, autoKey };
  }, [
    selectedConversation?.id,
    selectedConversation?.channelId,
    selectedConversation?.kind,
    selectedConversation?.userId,
    selectedConversation?.user_id,
    selectedConversation?.idUsuario,
    selectedConversation?.celular,
    selectedConversation?.phone,
    selectedConversation?.phoneNumber,
    selectedConversation?.email,
    channels,
    sendableChannels,
    messages,
    userData?.id,
    userData?.nome,
    userData?.master,
    permData?.blockedOutboundChannelIds,
  ]);

  const onComposeChannelChangeRef = useRef(onComposeChannelChange);
  useEffect(() => {
    onComposeChannelChangeRef.current = onComposeChannelChange;
  }, [onComposeChannelChange]);

  useEffect(() => {
    if (typeof onComposeChannelChangeRef.current === "function") {
      onComposeChannelChangeRef.current(selectedChannel);
    }
  }, [selectedChannel]);

  // Ao apagar o texto do input, desvincular o template selecionado (evita enviar template ao enviar mensagem vazia/editada).
  useEffect(() => {
    if (!composerHasTextPayload(messageRaw)) {
      setSelectedTemplate(null);
      setPendingVideoInviteButton(null);
    }
  }, [messageRaw]);

  // Ao apagar o @ ou o nome do usuário do input, desvincular transferTo para não transferir se o usuário foi removido do texto.
  useEffect(() => {
    if (!transferTo) return;
    const plain = messagePlainText.replace(/\u00a0/g, " ");
    const lastAtPos = plain.lastIndexOf("@");
    if (lastAtPos < 0) {
      setTransferTo(null);
      return;
    }
    const afterAt = plain.slice(lastAtPos + 1);
    const label = transferTo.label || "";
    const stillMentioned = afterAt === label || afterAt === label + " " || afterAt.startsWith(label + " ");
    if (!stillMentioned) {
      setTransferTo(null);
    }
  }, [messagePlainText, transferTo]);

  // Rascunho legado (só texto) ou race de carga: reconstrói transferTo a partir de @Nome.
  useEffect(() => {
    if (transferTo) return;
    if (!messagePlainText.includes("@")) return;
    if (!mentionOptions.length) return;
    const resolved = resolveTransferToFromMentionText(messagePlainText, mentionOptions);
    if (resolved) setTransferTo(resolved);
  }, [messagePlainText, mentionOptions, transferTo]);

  // Carrega sugestões (templates + respostas pré-definidas) ao mudar conversa, canal de envio ou tipo da solicitação.
  useEffect(() => {
    let isCancelled = false;

    async function loadSuggestions() {
      if (!selectedConversation?.id) return;
      if (selectedConversation?.kind === "personal") {
        if (!isCancelled) {
          setSuggestions({ templates: [], quickReplies: [] });
          setIsLoadingSuggestions(false);
          setSuggestionsError(null);
        }
        return;
      }
      if (channelType !== "WhatsApp") {
        if (!isCancelled) {
          setSuggestions({ templates: [], quickReplies: [] });
          setIsLoadingSuggestions(false);
          setSuggestionsError(null);
        }
        return;
      }

      try {
        setIsLoadingSuggestions(true);
        setSuggestionsError(null);
        const params = {
          originId: selectedChannel ?? selectedConversation?.channelId ?? undefined,
          typeId: selectedConversation?.typeId ?? undefined,
        };
        const data = await getConversationMessageSuggestions(selectedConversation.id, params);
        if (isCancelled) return;
        setSuggestions({
          templates: Array.isArray(data?.templates) ? data.templates : [],
          quickReplies: Array.isArray(data?.quickReplies) ? data.quickReplies : [],
          originId: data?.originId ?? null,
        });
      } catch (err) {
        if (isCancelled) return;
        console.error("Erro ao carregar sugestões de mensagens:", err);
        setSuggestionsError(err?.message || "Erro ao carregar sugestões de mensagens.");
      } finally {
        if (!isCancelled) {
          setIsLoadingSuggestions(false);
        }
      }
    }

    loadSuggestions();

    return () => {
      isCancelled = true;
    };
  }, [selectedConversation?.id, selectedConversation?.typeId, selectedConversation?.channelId, channelType, selectedChannel]);

  // Janela de 24h do WhatsApp é do cliente no canal (não só desta solicitação).
  const requiresTemplate = useMemo(() => {
    if (channelType !== "WhatsApp") return false;
    if (isWhatsAppQrChannelTipo(selectedChannelTipo)) return false;

    const conversationFamily = channelTypeToMessagingWindowFamily(
      selectedConversation?.channelType,
      null
    );
    const lastClientDate = resolveMessagingWindowLastInboundDate({
      messages,
      messagingWindows: selectedConversation?.messagingWindows,
      channelFamily: "whatsapp",
      conversationChannelFamily: conversationFamily,
    });

    if (!lastClientDate) {
      // Sem inbound do cliente no WhatsApp: tipicamente precisa de template para iniciar.
      return true;
    }

    return getWhatsAppWindowState(lastClientDate).status === "expired";
  }, [
    channelType,
    selectedChannelTipo,
    messages,
    selectedConversation?.messagingWindows,
    selectedConversation?.channelType,
  ]);

  const metaWindowState = useMemo(() => {
    if (!isMetaMessagingWindowChannel(channelType, selectedChannelTipo)) return null;
    const family = channelTypeToMessagingWindowFamily(channelType, selectedChannelTipo);
    if (!family || family === "whatsapp") return null;
    const conversationFamily = channelTypeToMessagingWindowFamily(
      selectedConversation?.channelType,
      null
    );
    const lastClientDate = resolveMessagingWindowLastInboundDate({
      messages,
      messagingWindows: selectedConversation?.messagingWindows,
      channelFamily: family,
      conversationChannelFamily: conversationFamily,
    });
    return getMetaMessagingWindowState(lastClientDate, {
      channelLabel: getMetaChannelLabel(channelType),
    });
  }, [
    channelType,
    selectedChannelTipo,
    messages,
    selectedConversation?.messagingWindows,
    selectedConversation?.channelType,
  ]);

  const metaWindowExpired = metaWindowState?.status === "expired";

  const composeSendBlocked = useMemo(() => {
    if (metaWindowExpired && isMetaMessagingWindowChannel(channelType)) {
      return true;
    }
    const personal =
      selectedConversation?.kind === "personal" ||
      String(selectedChannel) === String(PERSONAL_WHATSAPP_CHANNEL_ID);
    if (channelType !== "WhatsApp" || personal) return false;
    const hasPayload = composerHasTextPayload(messageRaw) || files.length > 0 || Boolean(audioURL);
    if (!hasPayload) return false;
    const templateRequiredForThisSend =
      requiresTemplate &&
      !selectedTemplate?.id &&
      (composerHasTextPayload(messageRaw) || (files.length === 0 && !audioURL));
    if (templateRequiredForThisSend) return true;
    return false;
  }, [
    metaWindowExpired,
    channelType,
    selectedConversation?.kind,
    selectedChannel,
    messageRaw,
    files.length,
    audioURL,
    requiresTemplate,
    selectedTemplate?.id,
  ]);

  /** Aviso compacto: só canal com tipo ≠ default, só para o responsável atual, até a 1ª mensagem externa enviada. */
  const showExternalSendBanner = useMemo(() => {
    if (selectedConversation?.kind === "personal") return false;
    if (String(selectedChannel) === String(INTERNAL_NOTE_CHANNEL_ID)) return false;
    if (String(selectedChannel) === String(PERSONAL_WHATSAPP_CHANNEL_ID)) return false;
    if (phoneSendBlocked) return false;
    if (!isComposerOutboundChannel(currentChannel, selectedChannelTipo)) return false;
    const myId = Number(userData?.id);
    const assigneeId = Number(
      selectedConversation?.userId ?? selectedConversation?.user_id ?? selectedConversation?.idUsuario
    );
    if (!Number.isFinite(myId) || myId <= 0) return false;
    if (!Number.isFinite(assigneeId) || assigneeId <= 0) return false;
    if (assigneeId !== myId) return false;
    if (hasMyExternalOutboundMessage(messages, myId)) return false;
    try {
      const k = EXTERNAL_CLIENT_BANNER_DONE_KEY(selectedConversation?.id);
      if (k && readWorkspaceSessionDynamic(k) === "1") return false;
    } catch (_) {
      /* ignore */
    }
    return true;
  }, [
    selectedChannel,
    selectedChannelTipo,
    currentChannel,
    selectedConversation?.id,
    selectedConversation?.kind,
    selectedConversation?.userId,
    selectedConversation?.user_id,
    selectedConversation?.idUsuario,
    userData?.id,
    messages,
    phoneSendBlocked,
  ]);

  const locationSendDisabledHelp = useMemo(() => {
    if (locationBlockedWhatsappGroup) return t("location.notSupportedWhatsappGroupQr");
    if (requiresTemplate) return t("input.locationOutside24h");
    if (phoneSendBlocked) return t("input.phoneRequiredSnackbar");
    return null;
  }, [locationBlockedWhatsappGroup, requiresTemplate, phoneSendBlocked, t]);

  const canShareContact = useMemo(() => {
    if (selectedConversation?.kind === "personal") return false;
    if (channelType !== "WhatsApp") return false;
    return isWhatsAppOfficialChannelTipo(selectedChannelTipo);
  }, [selectedConversation?.kind, channelType, selectedChannelTipo]);

  const contactSendDisabledHelp = useMemo(() => {
    if (!canShareContact) return t("shareContact.notSupportedChannel");
    if (requiresTemplate) return t("shareContact.outside24h");
    if (phoneSendBlocked) return t("input.phoneRequiredSnackbar");
    return null;
  }, [canShareContact, requiresTemplate, phoneSendBlocked, t]);

  useEffect(() => {
    if (locationConfirmOpen) setLocationConfirmMapFailed(false);
  }, [locationConfirmOpen]);

  const handleGenerateSuggestion = async () => {
    if (
      !selectedConversation?.id ||
      isGeneratingSuggestion ||
      !hasHistory
    )
      return;
    setIsGeneratingSuggestion(true);
    try {
      const extra = suggestionExtrasRef.current || {};
      const body = {
        draft: messagePlainText.trim() || "",
        channelName,
        channelType,
        channelId: selectedChannel,
      };
      if (extra.focusProductIds?.length) body.focusProductIds = extra.focusProductIds;
      if (extra.focusLineItemIds?.length) body.focusLineItemIds = extra.focusLineItemIds;
      const response = await generateSuggestedReply(selectedConversation.id, body);
      suggestionExtrasRef.current = { focusProductIds: [], focusLineItemIds: [] };
      const suggestion =
        response?.suggestion || response?.data?.suggestion || "";
      if (suggestion) {
        setMessage(suggestion);
      }
    } catch (error) {
      // manter simples, seguindo padrão atual
      // eslint-disable-next-line no-alert
      alert(
        "Não foi possível gerar uma sugestão de resposta agora. Tente novamente em instantes."
      );
      // eslint-disable-next-line no-console
      console.error("Erro ao gerar sugestão de resposta com IA:", error);
    } finally {
      setIsGeneratingSuggestion(false);
    }
  };

  const renderChannelComponent = () => {
    switch (channelType) {
      case "WhatsApp":
        return (
          <MessageWhatsAppChannel
            message={messageRaw}
            setMessage={setMessage}
            selectedChannel={selectedChannel}
            channelType={channelType}
            setSelectedChannel={handleChannelChange}
            isPersonalWhatsappThread={isPersonalWhatsappThread}
            formatOptions={formatOptions}
            startRecording={startRecording}
            openFilePicker={openFilePicker}
            onOpenProductPicker={openProductPickerFromMenu}
            handleEmojiPickerOpen={handleEmojiPickerOpen}
            handleSendMessage={handleSendMessage}
            file={files[0]}
            files={files}
            audioURL={audioURL}
            onGenerateSuggestion={handleGenerateSuggestion}
            isGeneratingSuggestion={isGeneratingSuggestion}
            hasHistory={hasHistory}
            suggestions={suggestions}
            isLoadingSuggestions={isLoadingSuggestions}
            suggestionsError={suggestionsError}
            requiresTemplate={requiresTemplate}
            selectedTemplate={selectedTemplate}
            onTemplateSelect={setSelectedTemplate}
            externalPickerOpen={pickerOverlayOpen}
            sendBlockedMissingPhone={phoneSendBlocked}
            sendComposeBlocked={composeSendBlocked}
            onSendCurrentLocation={handleSendCurrentLocation}
            locationSending={locationSending}
            locationSendDisabled={requiresTemplate || phoneSendBlocked || locationBlockedWhatsappGroup}
            locationSendDisabledHelp={locationSendDisabledHelp}
            showPoll={Boolean(qrCaps?.poll)}
            onStartPoll={() =>
              setPollDraft((p) => p || { question: "", options: ["", ""], selectableCount: 1 })
            }
            pollDisabled={requiresTemplate || phoneSendBlocked}
            pollDisabledHelp={
              requiresTemplate
                ? t("input.attachOutside24h")
                : phoneSendBlocked
                  ? t("input.phoneRequiredSnackbar")
                  : null
            }
            onShareContact={canShareContact ? handleOpenShareContact : undefined}
            contactSendDisabled={requiresTemplate || phoneSendBlocked || !canShareContact}
            contactSendDisabledHelp={contactSendDisabledHelp}
            onStartVideo={() => setStartVideoDialogOpen(true)}
            videoSending={videoSending}
            videoSendDisabled={videoInviteDisabled}
            videoSendDisabledHelp={videoSendDisabledHelp}
            videoMenuLabel={t("video.inviteXpro", "Convidar para vídeo XPro")}
            onJoinVideo={handleJoinActiveVideo}
            joinVideoSending={joinVideoSending}
            showJoinVideo={Boolean(activeVideoRoom?.joinUrl || activeVideoRoom?.agentJoinUrl)}
            onStartGoogleMeet={handleStartGoogleMeet}
            googleMeetSending={googleMeetSending}
            googleMeetDisabled={videoInviteDisabled}
            googleMeetDisabledHelp={videoSendDisabledHelp}
            isRecording={isRecording}
            recordingDuration={recordingDuration}
            stopRecording={stopRecording}
            discardRecording={discardRecording}
            onRemoveAudio={handleRemoveAudio}
            clientConversation={selectedConversation}
            conversationMessages={messages}
          />
        );
      case "Email":
        return (
          <MessageEmailChannel
            message={messageRaw}
            setMessage={setMessage}
            selectedChannel={selectedChannel}
            channelType={channelType}
            setSelectedChannel={handleChannelChange}
            isRecording={isRecording}
            startRecording={startRecording}
            stopRecording={stopRecording}
            openFilePicker={openFilePicker}
            handleEmojiPickerOpen={handleEmojiPickerOpen}
            handleSendMessage={handleSendMessage}
            file={files[0]}
            files={files}
            audioURL={audioURL}
            isMobile={isMobile}
            isExpanded={isExpanded}
            setIsExpanded={setIsExpanded}
            handleRemoveFile={handleRemoveFile}
            onGenerateSuggestion={handleGenerateSuggestion}
            isGeneratingSuggestion={isGeneratingSuggestion}
            hasHistory={hasHistory}
            recipientEmail={selectedConversation?.email}
            messages={messages}
            emailSendError={emailSendError}
            emailSending={emailSending}
            onDismissEmailError={() => setEmailSendError("")}
            clientConversation={selectedConversation}
            conversationMessages={messages}
            onStartVideo={() => setStartVideoDialogOpen(true)}
            videoSending={videoSending}
            videoSendDisabled={videoInviteDisabled}
            videoSendDisabledHelp={videoSendDisabledHelp}
            videoMenuLabel={t("video.inviteXpro", "Convidar para vídeo XPro")}
            onJoinVideo={handleJoinActiveVideo}
            joinVideoSending={joinVideoSending}
            showJoinVideo={Boolean(activeVideoRoom?.joinUrl || activeVideoRoom?.agentJoinUrl)}
            onStartGoogleMeet={handleStartGoogleMeet}
            googleMeetSending={googleMeetSending}
            googleMeetDisabled={videoInviteDisabled}
            googleMeetDisabledHelp={videoSendDisabledHelp}
          />
        );
      case "Phone":
      case "Telefone": {
        const userPhone =
          String(userData?.celular || userData?.telefone || userData?.phone || "").trim() || "";
        const customerPhones = buildCustomerDialDestinations(selectedConversation).map(
          (p) => p.e164
        );
        return (
          <MessagePhoneChannel
            selectedChannel={selectedChannel}
            setSelectedChannel={handleChannelChange}
            conversationId={selectedConversation?.id}
            userPhone={userPhone}
            customerPhones={customerPhones}
            origin={origin}
            setOrigin={setOrigin}
            destination={destination}
            setDestination={setDestination}
            handleSendMessage={handleSendMessage}
            isMobile={isMobile}
            clientConversation={selectedConversation}
            conversationMessages={messages}
          />
        );
      }
      case "Messenger":
      case "Instagram":
      case "Facebook Comentários":
      case "Instagram Comentários":
        return (
          <MessageMessengerChannel
            message={messageRaw}
            setMessage={setMessage}
            selectedChannel={selectedChannel}
            channelType={channelType}
            setSelectedChannel={handleChannelChange}
            formatOptions={formatOptions}
            isRecording={isRecording}
            recordingDuration={recordingDuration}
            startRecording={startRecording}
            stopRecording={stopRecording}
            discardRecording={discardRecording}
            openFilePicker={openFilePicker}
            handleEmojiPickerOpen={handleEmojiPickerOpen}
            handleSendMessage={handleSendMessage}
            file={files[0]}
            files={files}
            audioURL={audioURL}
            onRemoveAudio={handleRemoveAudio}
            onGenerateSuggestion={handleGenerateSuggestion}
            isGeneratingSuggestion={isGeneratingSuggestion}
            hasHistory={hasHistory}
            onStartVideo={() => setStartVideoDialogOpen(true)}
            videoSending={videoSending}
            videoSendDisabled={videoInviteDisabled}
            videoSendDisabledHelp={videoSendDisabledHelp}
            videoMenuLabel={t("video.inviteXpro", "Convidar para vídeo XPro")}
            onJoinVideo={handleJoinActiveVideo}
            joinVideoSending={joinVideoSending}
            showJoinVideo={Boolean(activeVideoRoom?.joinUrl || activeVideoRoom?.agentJoinUrl)}
            onStartGoogleMeet={handleStartGoogleMeet}
            googleMeetSending={googleMeetSending}
            googleMeetDisabled={videoInviteDisabled}
            googleMeetDisabledHelp={videoSendDisabledHelp}
            clientConversation={selectedConversation}
            conversationMessages={messages}
            metaWindowState={
              channelType === "Messenger" || channelType === "Instagram"
                ? metaWindowState
                : null
            }
          />
        );
      case "SMS":
        return (
          <MessageSMSChannel
            message={messageRaw}
            setMessage={setMessage}
            selectedChannel={selectedChannel}
            channelType={channelType}
            setSelectedChannel={handleChannelChange}
            formatOptions={formatOptions}
            isRecording={isRecording}
            recordingDuration={recordingDuration}
            startRecording={startRecording}
            stopRecording={stopRecording}
            discardRecording={discardRecording}
            openFilePicker={openFilePicker}
            handleEmojiPickerOpen={handleEmojiPickerOpen}
            handleSendMessage={handleSendMessage}
            file={files[0]}
            files={files}
            audioURL={audioURL}
            onRemoveAudio={handleRemoveAudio}
            onGenerateSuggestion={handleGenerateSuggestion}
            isGeneratingSuggestion={isGeneratingSuggestion}
            hasHistory={hasHistory}
            sendBlockedMissingPhone={phoneSendBlocked}
            clientConversation={selectedConversation}
            conversationMessages={messages}
            onStartVideo={() => setStartVideoDialogOpen(true)}
            videoSending={videoSending}
            videoSendDisabled={videoInviteDisabled}
            videoSendDisabledHelp={videoSendDisabledHelp}
            videoMenuLabel={t("video.inviteXpro", "Convidar para vídeo XPro")}
            onJoinVideo={handleJoinActiveVideo}
            joinVideoSending={joinVideoSending}
            showJoinVideo={Boolean(activeVideoRoom?.joinUrl || activeVideoRoom?.agentJoinUrl)}
            onStartGoogleMeet={handleStartGoogleMeet}
            googleMeetSending={googleMeetSending}
            googleMeetDisabled={videoInviteDisabled}
            googleMeetDisabledHelp={videoSendDisabledHelp}
          />
        );
      case "App":
        return (
          <MessageAppChannel
            message={messageRaw}
            setMessage={setMessage}
            selectedChannel={selectedChannel}
            channelType={channelType}
            setSelectedChannel={handleChannelChange}
            formatOptions={formatOptions}
            startRecording={startRecording}
            openFilePicker={openFilePicker}
            onOpenProductPicker={openProductPickerFromMenu}
            handleEmojiPickerOpen={handleEmojiPickerOpen}
            handleSendMessage={handleSendMessage}
            file={files[0]}
            files={files}
            audioURL={audioURL}
            onGenerateSuggestion={handleGenerateSuggestion}
            isGeneratingSuggestion={isGeneratingSuggestion}
            hasHistory={hasHistory}
            sendBlockedMissingPhone={phoneSendBlocked}
            isRecording={isRecording}
            recordingDuration={recordingDuration}
            stopRecording={stopRecording}
            discardRecording={discardRecording}
            onRemoveAudio={handleRemoveAudio}
            clientConversation={selectedConversation}
            conversationMessages={messages}
            {...(isWhatsAppQrChannelTipo(selectedChannelTipo)
              ? {
                  onSendCurrentLocation: handleSendCurrentLocation,
                  locationSending,
                  locationSendDisabled: phoneSendBlocked || locationBlockedWhatsappGroup,
                  locationSendDisabledHelp,
                }
              : {})}
          />
        );
      case "Wordpress":
        return (
          <MessageWordpressChannel
            message={messageRaw}
            setMessage={setMessage}
            selectedChannel={selectedChannel}
            channelType={channelType}
            setSelectedChannel={handleChannelChange}
            formatOptions={formatOptions}
            isRecording={isRecording}
            recordingDuration={recordingDuration}
            startRecording={startRecording}
            stopRecording={stopRecording}
            discardRecording={discardRecording}
            openFilePicker={openFilePicker}
            handleEmojiPickerOpen={handleEmojiPickerOpen}
            handleSendMessage={handleSendMessage}
            file={files[0]}
            files={files}
            audioURL={audioURL}
            onRemoveAudio={handleRemoveAudio}
            onGenerateSuggestion={handleGenerateSuggestion}
            isGeneratingSuggestion={isGeneratingSuggestion}
            hasHistory={hasHistory}
            clientConversation={selectedConversation}
            conversationMessages={messages}
          />
        );
      default:
        return (
          <MessageInputField
            message={messageRaw}
            setMessage={setMessage}
            selectedChannel={selectedChannel}
            channelType={channelType}
            setSelectedChannel={handleChannelChange}
            isPersonalWhatsappThread={isPersonalWhatsappThread}
            formatOptions={formatOptions}
            isRecording={isRecording}
            recordingDuration={recordingDuration}
            startRecording={startRecording}
            stopRecording={stopRecording}
            discardRecording={discardRecording}
            openFilePicker={openFilePicker}
            onOpenProductPicker={openProductPickerFromMenu}
            handleEmojiPickerOpen={handleEmojiPickerOpen}
            handleSendMessage={handleSendMessage}
            file={files[0]}
            files={files}
            audioURL={audioURL}
            onRemoveAudio={handleRemoveAudio}
            onGenerateSuggestion={handleGenerateSuggestion}
            isGeneratingSuggestion={isGeneratingSuggestion}
            hasHistory={hasHistory}
            sendBlockedMissingPhone={phoneSendBlocked}
            clientModeOnly={!isMobile && composeMode === COMPOSE_MODES.CLIENT}
            onRemoveFile={handleRemoveFile}
            clientConversation={selectedConversation}
            conversationMessages={messages}
            onStartVideo={() => setStartVideoDialogOpen(true)}
            videoSending={videoSending}
            videoSendDisabled={videoInviteDisabled}
            videoSendDisabledHelp={videoSendDisabledHelp}
            videoMenuLabel={t("video.inviteXpro", "Convidar para vídeo XPro")}
            onJoinVideo={handleJoinActiveVideo}
            joinVideoSending={joinVideoSending}
            showJoinVideo={Boolean(activeVideoRoom?.joinUrl || activeVideoRoom?.agentJoinUrl)}
            onStartGoogleMeet={handleStartGoogleMeet}
            googleMeetSending={googleMeetSending}
            googleMeetDisabled={videoInviteDisabled}
            googleMeetDisabledHelp={videoSendDisabledHelp}
          />
        );
    }
  };

  const handleChannelChange = (channelId) => {
    if (String(channelId) !== String(INTERNAL_NOTE_CHANNEL_ID)) {
      lastClientChannelRef.current = channelId;
    }
    setSelectedChannel(channelId);
    writeWorkspaceSessionDynamic(`selectedChannel${selectedConversation?.id}`, String(channelId));
  };

  const handleComposeModeChange = (mode) => {
    setComposeMode(mode);
    if (mode === COMPOSE_MODES.INTERNAL) {
      if (String(selectedChannel) !== String(INTERNAL_NOTE_CHANNEL_ID)) {
        lastClientChannelRef.current = selectedChannel;
      }
      setSelectedChannel(INTERNAL_NOTE_CHANNEL_ID);
      writeWorkspaceSessionDynamic(
        `selectedChannel${selectedConversation?.id}`,
        String(INTERNAL_NOTE_CHANNEL_ID)
      );
      return;
    }
    const restore = resolveClientComposeChannelRestore({
      lastClientChannel: lastClientChannelRef.current,
      savedChannel: readWorkspaceSessionDynamic(
        `selectedChannel${selectedConversation?.id}`
      ),
      conversationChannelId: selectedConversation?.channelId,
      defaultChannelId: resolveDefaultReplyChannel(
        messages,
        selectedConversation,
        sendableChannels
      ),
    });
    const restoreBlocked = isOutboundChannelIdBlocked(
      restore,
      permData?.blockedOutboundChannelIds,
      { isMaster: Number(userData?.master) === 1 }
    );
    const nextChannel = restoreBlocked
      ? resolveDefaultReplyChannel(messages, selectedConversation, sendableChannels)
      : restore;
    if (nextChannel != null) {
      lastClientChannelRef.current = nextChannel;
      setSelectedChannel(nextChannel);
      writeWorkspaceSessionDynamic(
        `selectedChannel${selectedConversation?.id}`,
        String(nextChannel)
      );
    }
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === "i") {
        e.preventDefault();
        handleComposeModeChange(
          composeMode === COMPOSE_MODES.INTERNAL ? COMPOSE_MODES.CLIENT : COMPOSE_MODES.INTERNAL
        );
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [composeMode]);

  useEffect(() => {
    if (String(selectedChannel) === String(INTERNAL_NOTE_CHANNEL_ID)) {
      setComposeMode(COMPOSE_MODES.INTERNAL);
    } else if (selectedChannel != null && selectedChannel !== "") {
      setComposeMode(COMPOSE_MODES.CLIENT);
    }
  }, [selectedChannel]);

  const handleSendCurrentLocation = () => {
    if (
      String(selectedChannel) !== String(INTERNAL_NOTE_CHANNEL_ID) &&
      channelTypeRequiresClientPhone(channelType) &&
      !hasClientPhoneRegistered(selectedConversation) &&
      !shouldSkipClientPhoneForWhatsappGroupSend({
        conversation: selectedConversation,
        messages,
        channelTipo: selectedChannelTipo,
        channelType,
      })
    ) {
      setPhoneBlockSnackbarOpen(true);
      return;
    }
    if (requiresTemplate) {
      return;
    }
    if (locationBlockedWhatsappGroup) {
      return;
    }
    if (!navigator.geolocation) {
      // eslint-disable-next-line no-alert
      alert(t("location.geolocationUnsupported"));
      return;
    }
    setLocationSending(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const latitude = pos.coords.latitude;
        const longitude = pos.coords.longitude;
        setLocationSending(false);
        setLocationDraft({ latitude, longitude });
        setLocationConfirmOpen(true);
      },
      (err) => {
        setLocationSending(false);
        const denied = err && err.code === err.PERMISSION_DENIED;
        // eslint-disable-next-line no-alert
        alert(denied ? t("location.geolocationDenied") : t("location.geolocationError"));
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 60_000 }
    );
  };

  const sendMeetingLinkMessage = useCallback(
    (text, { buttonMessage } = {}) => {
      if (!text || typeof onSendMessage !== "function") return;
      onSendMessage({
        message: String(text),
        files: [],
        audioURL: null,
        channel: selectedChannel,
        channelType,
        ...(showWebchatTransport && webchatTransport ? { transport: webchatTransport } : {}),
        ...(buttonMessage ? { buttonMessage } : {}),
        ...(transferTo && { transferTo }),
      });
      try {
        const k = EXTERNAL_CLIENT_BANNER_DONE_KEY(selectedConversation?.id);
        if (k) writeWorkspaceSessionDynamic(k, "1");
      } catch (_) {
        /* ignore */
      }
    },
    [
      onSendMessage,
      selectedChannel,
      channelType,
      transferTo,
      selectedConversation?.id,
      showWebchatTransport,
      webchatTransport,
    ]
  );

  const videoInviteDisabled =
    selectedConversation?.kind === "personal" ||
    String(selectedChannel) === String(INTERNAL_NOTE_CHANNEL_ID) ||
    selectedChannelTipo === CHANNEL_TYPES.TWILIO_VIDEO;

  // Entrar em sala ativa não depende de telefone / janela 24h / envio.
  const videoJoinDisabled = selectedConversation?.kind === "personal";

  const videoActionsDisabled = videoInviteDisabled;

  // Ajuda do menu: convite não envia sozinho; bloqueios de janela/telefone valem só se o agente for enviar pelo canal.
  const videoSendDisabledHelp = null;

  const channelAllowsInviteComposeText = useMemo(() => {
    if (String(selectedChannel) === String(INTERNAL_NOTE_CHANNEL_ID)) return false;
    if (String(selectedChannel) === String(PERSONAL_WHATSAPP_CHANNEL_ID)) return true;
    if (selectedChannelTipo == null) return false;
    if (!isComposerOutboundChannel(currentChannel, selectedChannelTipo)) return false;
    if (
      selectedChannelTipo === CHANNEL_TYPES.TWILIO_PHONE ||
      selectedChannelTipo === CHANNEL_TYPES.PABX ||
      selectedChannelTipo === CHANNEL_TYPES.TWILIO_VIDEO
    ) {
      return false;
    }
    if (requiresTemplate || metaWindowExpired) return false;
    return true;
  }, [
    selectedChannel,
    selectedChannelTipo,
    requiresTemplate,
    metaWindowExpired,
  ]);

  const buildVideoInviteComposerText = useCallback(
    (joinUrl, { includeUrl = true, requireCamera = false } = {}) => {
      const body = requireCamera
        ? t(
            "video.inviteBodyOutboundCameraRequired",
            "Esta chamada exige câmera ligada. Toque no botão abaixo para entrar com o vídeo ativo — sem câmera neste dispositivo, não é possível participar."
          )
        : t(
            "video.inviteBodyOutbound",
            "Toque no botão abaixo para entrar na chamada de vídeo com nossa equipe."
          );
      if (!includeUrl) return body;
      return `${body}\n${joinUrl}`;
    },
    [t]
  );

  const buildVideoInviteButtonMessage = useCallback(
    (joinUrl, { requireCamera = false } = {}) => {
      const cta = requireCamera
        ? t("video.inviteCtaOutboundCameraRequired", "Entrar com a câmera")
        : t("video.inviteCtaOutbound", "Entrar na chamada");
      return `${cta}|${joinUrl}`;
    },
    [t]
  );

  const channelSupportsWhatsAppInviteButton = useMemo(() => {
    if (String(selectedChannel) === String(PERSONAL_WHATSAPP_CHANNEL_ID)) return true;
    if (channelType === "WhatsApp") return true;
    return (
      isWhatsAppOfficialChannelTipo(selectedChannelTipo) ||
      isWhatsAppQrChannelTipo(selectedChannelTipo)
    );
  }, [selectedChannel, channelType, selectedChannelTipo]);

  const prefillVideoInviteInComposer = useCallback(
    (joinUrl, { requireCamera = false } = {}) => {
      if (!joinUrl || !channelAllowsInviteComposeText) return false;
      const withButton = channelSupportsWhatsAppInviteButton;
      setMessage(
        buildVideoInviteComposerText(joinUrl, {
          includeUrl: !withButton,
          requireCamera,
        })
      );
      if (withButton) {
        setPendingVideoInviteButton({
          buttonMessage: buildVideoInviteButtonMessage(joinUrl, { requireCamera }),
          joinUrl,
        });
      } else {
        setPendingVideoInviteButton(null);
      }
      return true;
    },
    [
      channelAllowsInviteComposeText,
      channelSupportsWhatsAppInviteButton,
      buildVideoInviteComposerText,
      buildVideoInviteButtonMessage,
    ]
  );

  const classifyVideoError = useCallback(
    (e, { forMeet = false } = {}) => {
      const code = e?.response?.data?.code || e?.code;
      const raw = String(e?.response?.data?.message || e?.userFacing || e?.message || "");
      if (code === "google_calendar_no_local_user") {
        return {
          kind: "meet_local_user",
          title: t("video.meetNeedsLocalUserTitle"),
          message: t("video.meetNeedsLocalUser"),
        };
      }
      if (code === "video.google_calendar_required" || (forMeet && /google calendar|conecte o google/i.test(raw))) {
        return {
          kind: "meet_calendar",
          title: t("video.meetNeedsCalendarTitle"),
          message: t("video.meetNeedsCalendar"),
        };
      }
      if (
        code === "video.channel_missing" ||
        /canal de Chamadas|Video Calls channel|canal de Llamadas|canale Chiamate/i.test(raw)
      ) {
        return {
          kind: "channel_missing",
          title: t("video.channelMissingTitle"),
          message: t("video.channelMissing"),
        };
      }
      if (
        code === "video.schema_missing" ||
        /twilio_video_rooms|doesn't exist|ER_NO_SUCH_TABLE|ainda está sendo preparado|still being prepared/i.test(
          raw
        )
      ) {
        return {
          kind: "schema_missing",
          title: t("video.schemaPreparingTitle"),
          message: t("video.schemaPreparing"),
        };
      }
      if (
        code === "video.session_inactive" ||
        code === "errors.auth.user_inactive" ||
        /usuário inválido ou inativo/i.test(raw)
      ) {
        return {
          kind: "session",
          title: t("video.sessionInactiveTitle", "Sessão inválida neste workspace"),
          message: t(
            "video.sessionInactive",
            "Sua sessão não é válida neste workspace. Confirme que está no workspace certo, entre novamente se preciso e abra a chamada pela solicitação."
          ),
        };
      }
      if (
        code === "video.session_required" ||
        code === "video.session_revoked" ||
        code === "video.session_invalid" ||
        code === "video.session_workspace_mismatch" ||
        code === "errors.auth.session_revoked" ||
        code === "errors.auth.token_missing" ||
        code === "errors.auth.token_invalid"
      ) {
        return {
          kind: "session",
          title: t("video.sessionInvalidTitle", "Não foi possível validar sua sessão"),
          message: t(
            "video.sessionInvalid",
            "Entre novamente no XPro no workspace correto e abra a chamada pela solicitação."
          ),
        };
      }
      return {
        kind: "generic",
        title: t("video.errorTitle"),
        message: raw || t("video.errorGeneric"),
      };
    },
    [t]
  );

  const handleConnectGoogleCalendar = useCallback(async () => {
    if (calendarConnecting) return;
    setCalendarConnecting(true);
    try {
      const r = await getGoogleCalendarOauthUrl();
      const url = r?.url || r?.data?.url;
      if (!url || typeof url !== "string") {
        throw new Error(t("video.errorGeneric"));
      }
      window.location.assign(url);
    } catch (err) {
      const code = err?.code || err?.response?.data?.code || err?.data?.code;
      if (code === "google_calendar_no_local_user") {
        setVideoGuide({
          kind: "meet_local_user",
          title: t("video.meetNeedsLocalUserTitle"),
          message: t("video.meetNeedsLocalUser"),
        });
      } else {
        setVideoGuide({
          kind: "generic",
          title: t("video.meetNeedsCalendarTitle"),
          message: err?.userFacing || err?.message || t("video.errorGeneric"),
        });
      }
      setCalendarConnecting(false);
    }
  }, [calendarConnecting, t]);

  const openAgentVideoUrl = useCallback(
    (joinUrl) => {
      if (!joinUrl) return;
      const agentName = String(userData?.nome || userData?.name || "").trim().slice(0, 80);
      openAgentVideoJoinUrl(joinUrl, {
        context: "support",
        name: agentName,
        onBlocked: () => {
          setVideoGuide({
            kind: "popup_blocked",
            title: t("video.popupBlockedTitle", "Pop-up bloqueado"),
            message: t(
              "video.popupBlocked",
              "O navegador bloqueou a abertura da chamada. Permita pop-ups para este site e toque em Entrar novamente."
            ),
          });
        },
      });
    },
    [userData?.nome, userData?.name, t]
  );

  useEffect(() => {
    const leadId = Number(selectedConversation?.id);
    if (!Number.isFinite(leadId) || leadId <= 0 || selectedConversation?.kind === "personal") {
      setActiveVideoRoom(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const room = await getVideoRoomByLead(leadId);
        if (!cancelled) setActiveVideoRoom(room || null);
      } catch {
        if (!cancelled) setActiveVideoRoom(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedConversation?.id, selectedConversation?.kind, messages?.length]);

  useEffect(() => {
    setGuestWaitingAlert(null);
    setHandoffAlert(null);
    setHandoffTargetUserId("");
  }, [selectedConversation?.id]);

  useEffect(() => {
    if (!socket) return undefined;
    const onVideoRoom = (raw) => {
      let payload = raw;
      if (typeof raw === "string") {
        try {
          payload = JSON.parse(raw);
        } catch {
          return;
        }
      }
      if (!payload || typeof payload !== "object") return;
      const action = String(payload.action || "");
      const leadId = Number(payload.leadId);
      const convId = Number(selectedConversation?.id);
      const myId = Number(userData?.id);

      if (action === "guest_waiting" && Number.isFinite(leadId) && leadId === convId) {
        setGuestWaitingAlert({
          roomId: Number(payload.roomId) || null,
          leadId,
        });
        try {
          playWaitBeep({ frequency: 780, durationMs: 160, volume: 0.05 });
        } catch {
          /* ignore */
        }
        (async () => {
          try {
            const room = await getVideoRoomByLead(leadId);
            setActiveVideoRoom(room || null);
          } catch {
            /* ignore */
          }
          queryClient.invalidateQueries(
            workspaceScopedQueryKey("conversationDetails", String(leadId))
          );
          if (typeof onRefetchConversationDetails === "function") {
            try {
              await onRefetchConversationDetails();
            } catch {
              /* ignore */
            }
          }
        })();
        return;
      }

      if (action === "handoff") {
        const toUserId = Number(payload.toUserId);
        const forMe = Number.isFinite(toUserId) && toUserId > 0 && toUserId === myId;
        if (!forMe) return;
        setHandoffAlert({
          roomId: Number(payload.roomId) || null,
          leadId: Number.isFinite(leadId) ? leadId : null,
          agentJoinUrl: payload.agentJoinUrl || null,
        });
        try {
          playWaitBeep({ frequency: 660, durationMs: 140, volume: 0.045 });
        } catch {
          /* ignore */
        }
        if (Number.isFinite(leadId) && leadId > 0 && leadId === convId) {
          (async () => {
            try {
              const room = await getVideoRoomByLead(leadId);
              setActiveVideoRoom(room || null);
            } catch {
              /* ignore */
            }
            queryClient.invalidateQueries(
              workspaceScopedQueryKey("conversationDetails", String(leadId))
            );
          })();
        }
      }
    };
    socket.on("video_room", onVideoRoom);
    return () => {
      socket.off("video_room", onVideoRoom);
    };
  }, [
    socket,
    selectedConversation?.id,
    userData?.id,
    queryClient,
    onRefetchConversationDetails,
  ]);

  const handoffAgentOptions = useMemo(() => {
    const me = Number(userData?.id);
    return humanUsers
      .filter((u) => Number(u.id) !== me)
      .map((u) => ({
        id: u.id,
        label: u.nome || u.name || `Usuário #${u.id}`,
      }))
      .filter((o) => o.label);
  }, [humanUsers, userData?.id]);

  const handleHandoffVideo = useCallback(async () => {
    const roomId = Number(guestWaitingAlert?.roomId || activeVideoRoom?.id);
    const toUserId = Number(handoffTargetUserId);
    if (!Number.isFinite(roomId) || roomId <= 0) return;
    if (!Number.isFinite(toUserId) || toUserId <= 0) return;
    if (handoffSending) return;
    setHandoffSending(true);
    try {
      await handoffVideoRoom(roomId, { toUserId });
      setHandoffTargetUserId("");
      setGuestWaitingAlert(null);
    } catch (e) {
      setVideoGuide(classifyVideoError(e));
    } finally {
      setHandoffSending(false);
    }
  }, [
    guestWaitingAlert?.roomId,
    activeVideoRoom?.id,
    handoffTargetUserId,
    handoffSending,
    classifyVideoError,
  ]);

  const handleJoinActiveVideo = useCallback(async () => {
    if (videoJoinDisabled || joinVideoSending) return;
    const leadId = Number(selectedConversation?.id);
    if (!Number.isFinite(leadId) || leadId <= 0) return;
    setJoinVideoSending(true);
    try {
      let room = activeVideoRoom;
      if (!room?.joinUrl && !room?.agentJoinUrl) {
        room = await getVideoRoomByLead(leadId);
        setActiveVideoRoom(room || null);
      }
      const joinUrl = room?.agentJoinUrl || room?.joinUrl;
      if (!joinUrl) throw new Error("missing_join_url");
      openAgentVideoUrl(joinUrl);
    } catch (e) {
      setVideoGuide(classifyVideoError(e));
      setActiveVideoRoom(null);
    } finally {
      setJoinVideoSending(false);
    }
  }, [
    videoJoinDisabled,
    joinVideoSending,
    selectedConversation?.id,
    activeVideoRoom,
    openAgentVideoUrl,
    classifyVideoError,
  ]);

  const handleStartXproVideo = useCallback(async (opts = {}) => {
    if (videoInviteDisabled || videoSending) return;
    const leadId = Number(selectedConversation?.id);
    if (!Number.isFinite(leadId) || leadId <= 0) return;
    const requireCamera = Boolean(opts.requireCamera);
    lastRequireCameraRef.current = requireCamera;
    setStartVideoDialogOpen(false);
    setVideoSending(true);
    try {
      const canSendInvite =
        channelSupportsWhatsAppInviteButton || channelAllowsInviteComposeText;
      const preferSendInChat = Boolean(canSendInvite);
      const result = await createVideoRoom({
        leadId,
        sendInChat: preferSendInChat,
        requireCamera,
        ...(opts.recordingEnabled != null
          ? { recordingEnabled: Boolean(opts.recordingEnabled) }
          : {}),
      });
      const joinUrl = result?.joinUrl || result?.agentJoinUrl;
      if (!joinUrl) throw new Error("missing_join_url");

      setActiveVideoRoom(result);
      if (result?.reused) {
        setVideoReuseSnackbarOpen(true);
      }
      queryClient.invalidateQueries(
        workspaceScopedQueryKey("conversationDetails", String(leadId))
      );

      const roomRequireCamera = Boolean(result?.requireCamera ?? requireCamera);
      const inviteBody =
        result?.inviteBody ||
        (roomRequireCamera
          ? t(
              "video.inviteBodyOutboundCameraRequired",
              "Esta chamada exige câmera ligada. Toque no botão abaixo para entrar com o vídeo ativo — sem câmera neste dispositivo, não é possível participar."
            )
          : t(
              "video.inviteBodyOutbound",
              "Toque no botão abaixo para entrar na chamada de vídeo com nossa equipe."
            ));
      const buttonMessage = buildVideoInviteButtonMessage(joinUrl, {
        requireCamera: roomRequireCamera,
      });

      const chatSent = Boolean(result?.chatSend?.sent);
      if (!chatSent) {
        if (channelAllowsInviteComposeText && channelSupportsWhatsAppInviteButton) {
          sendMeetingLinkMessage(inviteBody, { buttonMessage });
          setPendingVideoInviteButton(null);
          setMessage("");
        } else {
          prefillVideoInviteInComposer(joinUrl, { requireCamera: roomRequireCamera });
        }
      } else {
        setPendingVideoInviteButton(null);
      }

      openAgentVideoUrl(result.agentJoinUrl || joinUrl);
    } catch (e) {
      setVideoGuide(classifyVideoError(e));
    } finally {
      setVideoSending(false);
    }
  }, [
    videoInviteDisabled,
    videoSending,
    selectedConversation?.id,
    classifyVideoError,
    queryClient,
    openAgentVideoUrl,
    prefillVideoInviteInComposer,
    channelAllowsInviteComposeText,
    channelSupportsWhatsAppInviteButton,
    buildVideoInviteButtonMessage,
    sendMeetingLinkMessage,
    t,
  ]);

  const handleStartGoogleMeet = useCallback(async () => {
    if (videoInviteDisabled || googleMeetSending) return;
    const leadId = Number(selectedConversation?.id);
    if (!Number.isFinite(leadId) || leadId <= 0) return;
    setGoogleMeetSending(true);
    try {
      const composeChannelId =
        String(selectedChannel) !== String(INTERNAL_NOTE_CHANNEL_ID) &&
        String(selectedChannel) !== String(PERSONAL_WHATSAPP_CHANNEL_ID) &&
        selectedChannelTipo !== CHANNEL_TYPES.TWILIO_VIDEO
          ? Number(selectedChannel)
          : undefined;
      const result = await createGoogleMeetLink({
        leadId,
        sendInChat: true,
        ...(Number.isFinite(composeChannelId) && composeChannelId > 0
          ? { channelId: composeChannelId }
          : {}),
      });
      const meetUrl = result?.meetUrl || result?.hangoutLink;
      if (!meetUrl) throw new Error("missing_meet_url");

      if (!result?.chatSend?.sent) {
        sendMeetingLinkMessage(
          t(
            "video.inviteMeetBodyOutbound",
            "Toque no botão abaixo para abrir a reunião no Google Meet."
          ),
          {
            buttonMessage: `${t("video.inviteMeetCtaOutbound", "Abrir Meet")}|${meetUrl}`,
          }
        );
      } else {
        queryClient.invalidateQueries(
          workspaceScopedQueryKey("conversationDetails", String(leadId))
        );
      }
      window.open(meetUrl, "_blank", "noopener,noreferrer");
    } catch (e) {
      setVideoGuide(classifyVideoError(e, { forMeet: true }));
    } finally {
      setGoogleMeetSending(false);
    }
  }, [
    videoInviteDisabled,
    googleMeetSending,
    selectedConversation?.id,
    selectedChannel,
    selectedChannelTipo,
    sendMeetingLinkMessage,
    classifyVideoError,
    queryClient,
    t,
  ]);

  const handleCancelLocationConfirm = () => {
    setLocationConfirmOpen(false);
    setLocationDraft(null);
  };

  const handleConfirmLocationSend = () => {
    if (!locationDraft) return;
    if (typeof onSendMessage === "function") {
      onSendMessage({
        message: "",
        files: [],
        audioURL: null,
        channel: selectedChannel,
        channelType,
        location: {
          latitude: locationDraft.latitude,
          longitude: locationDraft.longitude,
        },
        ...(showWebchatTransport && webchatTransport ? { transport: webchatTransport } : {}),
        ...(transferTo && { transferTo }),
      });
      if (String(selectedChannel) !== String(INTERNAL_NOTE_CHANNEL_ID)) {
        try {
          const k = EXTERNAL_CLIENT_BANNER_DONE_KEY(selectedConversation?.id);
          if (k) writeWorkspaceSessionDynamic(k, "1");
        } catch (_) {
          /* ignore */
        }
      }
    }
    setLocationConfirmOpen(false);
    setLocationDraft(null);
  };

  const handleOpenShareContact = () => {
    if (
      String(selectedChannel) !== String(INTERNAL_NOTE_CHANNEL_ID) &&
      channelTypeRequiresClientPhone(channelType) &&
      !hasClientPhoneRegistered(selectedConversation) &&
      !shouldSkipClientPhoneForWhatsappGroupSend({
        conversation: selectedConversation,
        messages,
        channelTipo: selectedChannelTipo,
        channelType,
      })
    ) {
      setPhoneBlockSnackbarOpen(true);
      return;
    }
    if (requiresTemplate || !canShareContact) return;
    setShareContactOpen(true);
  };

  const handleConfirmShareContact = (contact) => {
    if (!contact || typeof onSendMessage !== "function") return;
    onSendMessage({
      message: "",
      files: [],
      audioURL: null,
      channel: selectedChannel,
      channelType,
      contact,
      ...(showWebchatTransport && webchatTransport ? { transport: webchatTransport } : {}),
      ...(transferTo && { transferTo }),
    });
    if (String(selectedChannel) !== String(INTERNAL_NOTE_CHANNEL_ID)) {
      try {
        const k = EXTERNAL_CLIENT_BANNER_DONE_KEY(selectedConversation?.id);
        if (k) writeWorkspaceSessionDynamic(k, "1");
      } catch (_) {
        /* ignore */
      }
    }
    setShareContactOpen(false);
  };

  const handleSendMessage = async (emailOverrides = {}, meta = {}) => {
    if (
      showWebchatTransport &&
      webchatTransport === "webchat" &&
      !canSendViaWebchat(webchatPresence)
    ) {
      setWebchatTransportOverride("whatsapp");
      return {
        ok: false,
        error: t(
          "webchatTransport.notVisible",
          "O cliente não está com o Web Chat visível. Envie pelo WhatsApp ou reenvie o link."
        ),
      };
    }
    const filledPollOptions = Array.isArray(pollDraft?.options)
      ? pollDraft.options.map((opt) => String(opt || "").trim()).filter(Boolean)
      : [];
    const pollQuestion = String(pollDraft?.question || "").trim();
    const canSendPoll = Boolean(qrCaps?.poll && pollQuestion && filledPollOptions.length >= 2);
    const hasContent =
      composerHasTextPayload(messageRaw) || files.length > 0 || Boolean(audioURL) || canSendPoll;
    const payloadChannel = meta.overrideChannel ?? selectedChannel;
    const payloadChannelType =
      String(payloadChannel) === String(PERSONAL_WHATSAPP_CHANNEL_ID)
        ? "WhatsApp"
        : String(payloadChannel) === String(INTERNAL_NOTE_CHANNEL_ID)
          ? "Xpro"
          : getChannelTypeById(channels, payloadChannel);
    const personalWa =
      selectedConversation?.kind === "personal" ||
      String(selectedChannel) === String(PERSONAL_WHATSAPP_CHANNEL_ID);
    const sendingInternal =
      String(payloadChannel) === String(INTERNAL_NOTE_CHANNEL_ID);

    if (
      hasContent &&
      !sendingInternal &&
      channelTypeRequiresClientPhone(payloadChannelType) &&
      !hasClientPhoneRegistered(selectedConversation) &&
      !shouldSkipClientPhoneForWhatsappGroupSend({
        conversation: selectedConversation,
        messages,
        channelTipo: selectedChannelTipo,
        channelType: payloadChannelType,
      })
    ) {
      setPhoneBlockSnackbarOpen(true);
      return;
    }

    if (
      hasContent &&
      channelType === "WhatsApp" &&
      !personalWa &&
      !sendingInternal
    ) {
      const templateRequiredForThisSend =
        requiresTemplate &&
        !selectedTemplate?.id &&
        (composerHasTextPayload(messageRaw) || (files.length === 0 && !audioURL));
      if (templateRequiredForThisSend) {
        setComposeRuleKey("template");
        setComposeRuleSnackbarOpen(true);
        return;
      }
    }

    if (
      hasContent &&
      !sendingInternal &&
      metaWindowExpired &&
      (payloadChannelType === "Messenger" || payloadChannelType === "Instagram")
    ) {
      setComposeRuleKey("metaWindow");
      setComposeRuleSnackbarOpen(true);
      return;
    }

    if (hasContent && !meta.forceClientSend && !sendingInternal) {
      if (whatsappGroupMentionIntent && !transferTo) {
        if (!hasSeenMentionHint(WS_KEY.WHATSAPP_GROUP_MENTION_HINT_SEEN, userData?.id)) {
          setMentionConfirmKind("whatsappFirst");
          setMentionConfirmOpen(true);
          return;
        }
      } else if (
        colleagueAtMentionIntent &&
        !hasSeenMentionHint(WS_KEY.COLLEAGUE_MENTION_HINT_SEEN, userData?.id)
      ) {
        setMentionConfirmKind("colleague");
        setMentionConfirmOpen(true);
        return;
      }
    }

    if (hasContent) {
      const plainOutgoing = composerPlainText(messageRaw, { trim: true });
      const pendingBtn = pendingVideoInviteButton?.buttonMessage;
      const pendingUrl = pendingVideoInviteButton?.joinUrl;
      const shouldAttachVideoButton =
        Boolean(pendingBtn) &&
        !sendingInternal &&
        (payloadChannelType === "WhatsApp" ||
          String(payloadChannel) === String(PERSONAL_WHATSAPP_CHANNEL_ID)) &&
        (!pendingUrl ||
          plainOutgoing.includes(pendingUrl) ||
          /entrar na chamada de v[ií]deo/i.test(plainOutgoing));

      const payload = {
        message: canSendPoll ? pollQuestion : messageRaw.trim(),
        files: files.length ? [...files] : [],
        audioURL: audioURL || null,
        channel: payloadChannel,
        channelType: payloadChannelType,
        ...(transferTo && { transferTo }),
        ...(selectedTemplate?.id && { templateId: selectedTemplate.id }),
        ...(shouldAttachVideoButton ? { buttonMessage: pendingBtn } : {}),
        ...mentionsForOutboundPayload({
          enabled: Boolean(qrGroupIdForMentions) && !sendingInternal,
          mentions: whatsappMentions,
          mentionNames: whatsappMentionNames,
          mentionAll: whatsappMentionAll,
          plainText: composerPlainText(canSendPoll ? pollQuestion : messageRaw, { trim: true }),
        }),
        ...(canSendPoll
          ? { type: "poll", pollOptions: filledPollOptions, selectableCount: pollDraft.selectableCount || 1 }
          : {}),
        ...(qrCaps?.sticker && files[0]?.type === "image/webp" ? { type: "sticker" } : {}),
        ...(quoteWhatsappIdFromMessage(quoteDraft)
          ? {
              quotedMessageId: quoteWhatsappIdFromMessage(quoteDraft),
              ...(quoteParticipantFromMessage(quoteDraft)
                ? { quotedParticipant: quoteParticipantFromMessage(quoteDraft) }
                : {}),
            }
          : {}),
        ...(showWebchatTransport && webchatTransport
          ? { transport: webchatTransport }
          : {}),
        ...(payloadChannelType === "Email" && {
          emailTo: emailOverrides.emailTo ?? selectedConversation?.email,
          ...(emailOverrides.emailSubject != null && { emailSubject: emailOverrides.emailSubject }),
          ...(emailOverrides.emailCc != null && { emailCc: emailOverrides.emailCc }),
        }),
      };
      if (typeof onSendMessage === "function") {
        if (payloadChannelType === "Email") {
          if (emailSendingRef.current) {
            return { ok: false, error: "Aguarde o envio terminar." };
          }
          const limitError = emailAttachmentLimitError(payload.files);
          if (limitError) {
            setEmailSendError(limitError);
            return { ok: false, error: limitError };
          }
          const toError = emailAddressListError(payload.emailTo, { label: "do destinatário" });
          if (toError) {
            setEmailSendError(toError);
            return { ok: false, error: toError };
          }
          if (payload.emailCc != null) {
            const ccValue = Array.isArray(payload.emailCc)
              ? payload.emailCc.join(",")
              : payload.emailCc;
            if (String(ccValue || "").trim()) {
              const ccError = emailAddressListError(ccValue, { label: "em cópia" });
              if (ccError) {
                setEmailSendError(ccError);
                return { ok: false, error: ccError };
              }
            }
          }
          emailSendingRef.current = true;
          setEmailSending(true);
          try {
            const result = await onSendMessage(payload);
            if (result && result.ok === false) {
              setEmailSendError(
                result.error ||
                  "Não foi possível enviar o e-mail. Nada foi enviado. Tente de novo."
              );
              return result;
            }
            setEmailSendError("");
          } finally {
            emailSendingRef.current = false;
            setEmailSending(false);
          }
        } else {
          onSendMessage(payload);
        }
        if (!sendingInternal) {
          setPollDraft(null);
          if (typeof onClearQuote === "function") onClearQuote();
          try {
            const k = EXTERNAL_CLIENT_BANNER_DONE_KEY(selectedConversation?.id);
            if (k) writeWorkspaceSessionDynamic(k, "1");
          } catch (_) {
            /* ignore */
          }
        }
      } else {
        console.error("onSendMessage não é uma função válida.");
      }
      if (meta.overrideChannel != null) {
        handleChannelChange(meta.overrideChannel);
      }
      if (selectedConversation?.id) {
        clearChatMessageDraft(selectedConversation.id, {
          ...draftContextRef.current,
          mode: draftModeForCompose(draftModeRef.current || composeMode),
        });
      }
      setMessage("");
      setFiles([]);
      setAudioURL(null);
      setTransferTo(null);
      setWhatsappMentions([]);
      setWhatsappMentionAll(false);
      setWhatsappMentionNames({});
      setSelectedTemplate(null);
      if (shouldAttachVideoButton) setPendingVideoInviteButton(null);
    }
  };

  const handleMentionSendToClient = () => {
    if (mentionConfirmKind === "whatsappFirst") {
      markMentionHintSeen(WS_KEY.WHATSAPP_GROUP_MENTION_HINT_SEEN, userData?.id);
    } else {
      markMentionHintSeen(WS_KEY.COLLEAGUE_MENTION_HINT_SEEN, userData?.id);
    }
    setMentionConfirmOpen(false);
    setMentionConfirmKind("colleague");
    handleSendMessage({}, { forceClientSend: true });
  };

  const handleMentionSaveAsInternal = () => {
    markMentionHintSeen(WS_KEY.COLLEAGUE_MENTION_HINT_SEEN, userData?.id);
    setMentionConfirmOpen(false);
    setMentionConfirmKind("colleague");
    handleSendMessage({}, {
      forceClientSend: true,
      overrideChannel: INTERNAL_NOTE_CHANNEL_ID,
    });
  };

  const openFilePicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = null; // Resetar o input de arquivo.
      fileInputRef.current.click();
    }
  };

  /** Mesmo fluxo de digitar # no campo: abre o seletor de produtos (protocolo + catálogo). */
  const openProductPickerFromMenu = useCallback(() => {
    setMessage((m) => {
      const ms = typeof m === "string" ? m : "";
      const lh = ms.lastIndexOf("#");
      if (lh >= 0 && !/\s/.test(ms.slice(lh + 1))) {
        return ms;
      }
      const sep = ms.length && !/\s$/.test(ms) ? " " : "";
      return `${ms}${sep}#`;
    });
  }, []);

  const handleFileUploadInput = (e) => {
    const selectedFiles = e.target.files ? Array.from(e.target.files) : [];
    if (selectedFiles.length) {
      handleFileUpload(selectedFiles);
    }
    e.target.value = "";
  };

  const handleFileUpload = (fileOrFiles) => {
    if (!fileOrFiles) return;
    const toAdd = Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles];
    if (channelType === "Email") {
      const limitError = emailAttachmentLimitError([...files, ...toAdd]);
      if (limitError) {
        setEmailSendError(limitError);
        return;
      }
      setEmailSendError("");
    }
    setFiles((prev) => [...prev, ...toAdd]);
  };

  const handleRemoveFile = (index) => {
    if (typeof index === "number") {
      setFiles((prev) => prev.filter((_, i) => i !== index));
    } else {
      setFiles([]);
    }
  };

  // Ao remover o áudio, limpar também o anexo (files) para evitar que o preview de arquivo fique visível
  const handleRemoveAudio = () => {
    if (audioURL && audioURL.startsWith("blob:")) {
      URL.revokeObjectURL(audioURL);
    }
    setAudioURL(null);
    setFiles([]);
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices) {
      alert("Gravação de áudio não é suportada neste navegador.");
      return;
    }
    try {
      if (!mp3RecorderRef.current) {
        mp3RecorderRef.current = new MicRecorder({ bitRate: 128 });
      }
      await mp3RecorderRef.current.start();
      setIsRecording(true);
      setRecordingDuration(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration((d) => d + 1);
      }, 1000);
    } catch (e) {
      console.error("Erro ao iniciar gravação:", e);
      alert("Não foi possível acessar o microfone. Verifique as permissões.");
    }
  };

  const stopRecording = () => {
    discardRequestedRef.current = false;
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    setRecordingDuration(0);
    if (!mp3RecorderRef.current) {
      setIsRecording(false);
      return;
    }
    mp3RecorderRef.current
      .stop()
      .getMp3()
      .then(([buffer, blob]) => {
        if (discardRequestedRef.current) {
          discardRequestedRef.current = false;
          setIsRecording(false);
          return;
        }
        const audioUrl = URL.createObjectURL(blob);
        setAudioURL(audioUrl);
        setIsRecording(false);
      })
      .catch((e) => {
        console.error("Erro ao finalizar gravação:", e);
        discardRequestedRef.current = false;
        setIsRecording(false);
      });
  };

  const discardRecording = () => {
    discardRequestedRef.current = true;
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    setRecordingDuration(0);
    if (!mp3RecorderRef.current) {
      setIsRecording(false);
      return;
    }
    mp3RecorderRef.current
      .stop()
      .getMp3()
      .then(([buffer, blob]) => {
        discardRequestedRef.current = false;
        setIsRecording(false);
      })
      .catch(() => {
        discardRequestedRef.current = false;
        setIsRecording(false);
      });
  };

  const handleEmojiPickerOpen = (event) => setEmojiAnchorEl(event.currentTarget);
  const handleEmojiPickerClose = () => setEmojiAnchorEl(null);
  const handleSelectEmoji = (emoji) =>
    setMessage((prev) => `${typeof prev === "string" ? prev : ""}${emoji}`);

  const filledPollOptions = Array.isArray(pollDraft?.options)
    ? pollDraft.options.map((opt) => String(opt || "").trim()).filter(Boolean)
    : [];
  const pollQuestion = String(pollDraft?.question || "").trim();
  const canSendPoll = Boolean(qrCaps?.poll && pollQuestion && filledPollOptions.length >= 2);
  const hasComposerPayload =
    composerHasTextPayload(messageRaw) || files.length > 0 || Boolean(audioURL) || canSendPoll;
  const isInternalNoteComposer =
    String(selectedChannel) === String(INTERNAL_NOTE_CHANNEL_ID) || channelType === "Xpro";
  const canRecordOnDesktop =
    channelType !== "Phone" && channelType !== "Email" && !isInternalNoteComposer;
  const showDesktopMicButton = canRecordOnDesktop && !hasComposerPayload && !isRecording;
  const isPersonalWhatsappThread =
    selectedConversation?.kind === "personal" ||
    String(selectedChannel) === String(PERSONAL_WHATSAPP_CHANNEL_ID);
  const sendAccent = isPersonalWhatsappThread ? "#128C7E" : null;
  const sendAccentHover = isPersonalWhatsappThread ? "#0f6f64" : null;
  /** E-mail/Telefone não embutem gravação na cápsula do canal; demais canais substituem só o campo de texto. */
  const voiceUiEmbeddedInChannel = channelType !== "Email" && channelType !== "Phone";

  return (
    <FileDropHandler onFileUpload={handleFileUpload}>
      {/* Respiro no rodapé (desktop também não pode ficar colado). */}
      <Box paddingBottom={isMobile ? 0 : 0.5}>
        {guestWaitingAlert || handoffAlert ? (
          <Alert
            severity={handoffAlert && !guestWaitingAlert ? "info" : "success"}
            sx={{ mb: 1 }}
            action={
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Button
                  color="inherit"
                  size="small"
                  disabled={joinVideoSending}
                  onClick={() => {
                    if (handoffAlert?.agentJoinUrl) {
                      openAgentVideoUrl(handoffAlert.agentJoinUrl);
                      setHandoffAlert(null);
                      return;
                    }
                    handleJoinActiveVideo();
                    setGuestWaitingAlert(null);
                    setHandoffAlert(null);
                  }}
                  sx={{ textTransform: "none", fontWeight: 700 }}
                >
                  {t("video.enterCall", "Entrar")}
                </Button>
              </Stack>
            }
          >
            <Typography variant="body2" fontWeight={700}>
              {handoffAlert && !guestWaitingAlert
                ? t("video.handoffReceivedTitle", "Chamada transferida para você")
                : t("video.guestWaitingTitle", "Cliente na chamada")}
            </Typography>
            {guestWaitingAlert && handoffAgentOptions.length ? (
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 1 }} alignItems="center">
                <FormControl size="small" sx={{ minWidth: 180, flex: 1 }}>
                  <InputLabel id="video-handoff-agent-label">
                    {t("video.handoffToLabel", "Passar para…")}
                  </InputLabel>
                  <Select
                    labelId="video-handoff-agent-label"
                    label={t("video.handoffToLabel", "Passar para…")}
                    value={handoffTargetUserId}
                    onChange={(e) => setHandoffTargetUserId(String(e.target.value))}
                  >
                    {handoffAgentOptions.map((opt) => (
                      <MenuItem key={opt.id} value={String(opt.id)}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!handoffTargetUserId || handoffSending}
                  onClick={handleHandoffVideo}
                  sx={{ textTransform: "none", fontWeight: 700, whiteSpace: "nowrap" }}
                >
                  {handoffSending
                    ? t("video.handoffSending", "Passando…")
                    : t("video.handoffConfirm", "Passar")}
                </Button>
              </Stack>
            ) : null}
          </Alert>
        ) : null}
        {(() => {
          const originId =
            selectedConversation?.channelId ??
            selectedConversation?.id_origem ??
            selectedConversation?.channel_id;
          const originCh = getChannelById(channels, originId);
          if (
            Number(originCh?.tipo) === CHANNEL_TYPES.RECLAME_AQUI &&
            String(originCh?.ra_mode || "").toLowerCase() !== "api"
          ) {
            return (
              <Alert severity="info" sx={{ mb: 1 }}>
                {t(
                  "input.reclameAquiScrapeBanner",
                  "Origem Reclame Aqui (monitoramento): responda por anotação interna ou outro canal — este modo não envia resposta ao site."
                )}
              </Alert>
            );
          }
          return null;
        })()}
        {phoneSendBlocked && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            {t("input.phoneRequiredBanner")}
          </Alert>
        )}
        {showExternalSendBanner && (
          <Typography
            component="div"
            variant="caption"
            sx={{
              display: "block",
              mb: 0.5,
              px: 0.25,
              lineHeight: 1.25,
              color: "text.secondary",
            }}
          >
            {t("input.externalSendBanner", {
              channel: channelName || channelType || "",
            })}
          </Typography>
        )}
        {quoteDraft ? (
          <Alert
            severity="info"
            sx={{ mb: 1 }}
            onClose={typeof onClearQuote === "function" ? onClearQuote : undefined}
          >
            {t("input.quotingMessage", {
              preview: String(quoteDraft.text || quoteDraft.id_msg_whatsapp || "").slice(0, 140),
            })}
          </Alert>
        ) : null}
        {pollDraft ? (
          <Paper
            variant="outlined"
            sx={{
              width: "100%",
              mb: 1,
              p: 1.5,
              borderRadius: 2,
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {t("input.pollTitle", "Enquete")}
              </Typography>
              <Button size="small" color="inherit" onClick={() => setPollDraft(null)}>
                {t("input.pollCancel", "Cancelar enquete")}
              </Button>
            </Stack>
            <Stack spacing={1.25}>
              <TextField
                size="small"
                fullWidth
                autoFocus
                label={t("input.pollQuestionLabel", "Pergunta")}
                value={pollDraft.question || ""}
                onChange={(e) => setPollDraft((prev) => ({ ...prev, question: e.target.value }))}
              />
              {pollDraft.options.map((opt, idx) => (
                <Stack key={idx} direction="row" spacing={0.5} alignItems="center">
                  <TextField
                    size="small"
                    fullWidth
                    label={t("input.pollOptionLabel", { n: idx + 1 })}
                    value={opt}
                    onChange={(e) =>
                      setPollDraft((prev) => {
                        const options = [...prev.options];
                        options[idx] = e.target.value;
                        return { ...prev, options };
                      })
                    }
                  />
                  {pollDraft.options.length > 2 ? (
                    <IconButton
                      size="small"
                      aria-label={t("input.pollRemoveOption", "Remover opção")}
                      onClick={() =>
                        setPollDraft((prev) => ({
                          ...prev,
                          options: prev.options.filter((_, i) => i !== idx),
                        }))
                      }
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  ) : null}
                </Stack>
              ))}
              <Button
                size="small"
                onClick={() => setPollDraft((prev) => ({ ...prev, options: [...prev.options, ""] }))}
                sx={{ alignSelf: "flex-start" }}
              >
                {t("input.pollAddOption", "Mais opção")}
              </Button>
              <Typography variant="caption" color="text.secondary">
                {t("input.pollQuestionHint")}
              </Typography>
              <Button
                variant="contained"
                disabled={!canSendPoll || phoneSendBlocked || composeSendBlocked}
                onClick={() => handleSendMessage()}
                startIcon={<Send />}
                sx={{ alignSelf: "flex-end" }}
              >
                {t("input.pollSend", "Enviar enquete")}
              </Button>
            </Stack>
          </Paper>
        ) : null}
        {showWebchatTransport ? (
          <WebchatTransportBar
            conversationId={selectedConversation?.id}
            presence={webchatPresence}
            transport={webchatTransport}
            onTransportChange={setWebchatTransportOverride}
            onPresenceRefresh={onRefetchConversationDetails}
          />
        ) : null}
        <Box 
          display="flex"
          alignItems="center"
          gap={isMobile ? 0.25 : 0.75}
          flexWrap="nowrap"
          sx={{
            justifyContent: "flex-start",
            width: "100%",
          }}
        >
          {selectedConversation?.kind !== "personal" && !isMobile && (
            <ComposeModeBar mode={composeMode} onChange={handleComposeModeChange} />
          )}
          {/* ChannelSelector agora é integrado por canal (layout padronizado). */}
          {/* {channelType === "Phone" ? (
            <MessagePhoneChannel
              selectedChannel={selectedChannel}
              handleChannelChange={handleChannelChange}
              originOptions={["computer", "(11) 91234-5678", "custom"]}
              userPhone="(11) 91234-5678" // Exemplo do telefone do usuário
              customerPhones={["(21) 99876-5432", "(31) 98765-4321"]} // Telefones do cliente
              origin={origin}
              setOrigin={setOrigin}
              destination={destination}
              setDestination={setDestination}
              handleSendMessage={handleSendMessage}
              isMobile={isMobile}
            />               
          ) : (<MessageInputField
              message={messageRaw}
              setMessage={setMessage}
              selectedChannel={selectedChannel}
              channelType={channelType}
              setSelectedChannel={handleChannelChange}
              formatOptions={formatOptions}
              isRecording={isRecording}
              startRecording={startRecording}
              stopRecording={stopRecording}
              openFilePicker={openFilePicker}
              handleEmojiPickerOpen={handleEmojiPickerOpen}
              handleSendMessage={handleSendMessage}
              file={files[0]}
              audioURL={audioURL}
          />)} */}
          <Box
            ref={mentionAnchorRef}
            sx={{
              flex: 1,
              minWidth: 0,
              width: "100%",
              minHeight: isMobile ? COMPOSER_MOBILE_ROW_HEIGHT_PX : COMPOSER_ROW_HEIGHT_PX,
              display: "flex",
              alignItems: "stretch",
              transition: "opacity 0.2s ease",
            }}
          >
            {voiceUiEmbeddedInChannel ? (
              renderChannelComponent()
            ) : isRecording ? (
              <RecordingIndicator
                durationSeconds={recordingDuration}
                onStop={stopRecording}
                onDiscard={discardRecording}
              />
            ) : audioURL ? (
              <AudioPreview audioURL={audioURL} onRemove={handleRemoveAudio} />
            ) : (
              renderChannelComponent()
            )}
          </Box>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            multiple
            onChange={handleFileUploadInput}
          />
          {(!isMobile && channelType !== "Phone" && channelType !== "Email" && !isExpanded) && (
            <Tooltip
              title={
                isRecording
                  ? t("input.sendAfterStopTooltip")
                  : showDesktopMicButton
                    ? t("input.recordVoiceAria")
                    : ""
              }
              enterDelay={400}
              disableHoverListener={!isRecording && !showDesktopMicButton}
            >
              <Box component="span" sx={{ display: "inline-flex", alignSelf: "center", flexShrink: 0 }}>
                <IconButton
                  onClick={showDesktopMicButton ? startRecording : handleSendMessage}
                  disabled={
                    phoneSendBlocked ||
                    composeSendBlocked ||
                    (showDesktopMicButton ? false : !hasComposerPayload)
                  }
                  aria-label={
                    showDesktopMicButton ? t("input.recordVoiceAria") : t("input.sendAria")
                  }
                  sx={{
                    ...composerPrimaryActionButtonSx({
                      accent: sendAccent || undefined,
                      accentHover: sendAccentHover || undefined,
                    }),
                    "&:hover": {
                      backgroundColor: sendAccentHover || "primary.dark",
                    },
                  }}
                >
                  {showDesktopMicButton ? <Mic /> : <Send />}
                </IconButton>
              </Box>
            </Tooltip>
          )}
        </Box>
        {!isExpanded && files.length > 0 && (
          <Box sx={{ width: "100%", mt: 0.5, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {files.map((file, index) => (
              <FilePreview
                key={`${file.name}-${file.size}-${index}`}
                file={file}
                onRemove={() => handleRemoveFile(index)}
              />
            ))}
          </Box>
        )}
        <Popover
          open={Boolean(emojiAnchorEl)}
          anchorEl={emojiAnchorEl}
          onClose={handleEmojiPickerClose}
          anchorOrigin={{ vertical: "top", horizontal: "left" }}
          transformOrigin={{ vertical: "bottom", horizontal: "left" }}
          slotProps={{
            paper: { sx: { maxHeight: "min(400px, 70vh)", overflow: "auto" } },
          }}
        >
          <EmojiPicker onSelectEmoji={handleSelectEmoji} />
        </Popover>
        {/* Lista de menções: overlay em Portal acima do input (sem roubar foco) */}
        {mentionOverlayPosition &&
          createPortal(
            <Box
              role="listbox"
              tabIndex={-1}
              onMouseDown={(e) => e.preventDefault()}
              sx={{
                position: "fixed",
                bottom: mentionOverlayPosition.bottom,
                left: mentionOverlayPosition.left,
                width: mentionOverlayPosition.width,
                maxHeight: "min(340px, 55vh)",
                overflow: "auto",
                bgcolor: "background.paper",
                boxShadow: 2,
                borderRadius: 1,
                zIndex: 1300,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              {mentionPickerActive && mentionOptions.length > 0
                ? mentionPickerSections
                  ? mentionPickerSections.map((section) => (
                      <Box key={section.id}>
                        <Box
                          sx={{
                            px: 1.5,
                            pt: 1,
                            pb: 0.75,
                            bgcolor: "background.paper",
                            borderBottom: "1px solid",
                            borderColor: "divider",
                            borderLeft: "3px solid",
                            borderLeftColor: section.id === "whatsapp" ? "success.main" : "divider",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 700,
                              letterSpacing: 0.3,
                              textTransform: "uppercase",
                              color: section.id === "whatsapp" ? "success.dark" : "text.secondary",
                            }}
                          >
                            {section.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.3 }}>
                            {section.hint}
                          </Typography>
                        </Box>
                        {section.options.map((opt) => {
                          const idx = mentionOptions.indexOf(opt);
                          return (
                            <MentionPickerRow
                              key={`${opt.type}-${opt.id}-${idx}`}
                              opt={opt}
                              highlighted={idx === mentionHighlightIndex}
                              onSelect={handleSelectMention}
                            />
                          );
                        })}
                      </Box>
                    ))
                  : mentionOptions.map((opt, idx) => (
                      <MentionPickerRow
                        key={`${opt.type}-${opt.id}-${idx}`}
                        opt={opt}
                        highlighted={idx === mentionHighlightIndex}
                        onSelect={handleSelectMention}
                        compact
                      />
                    ))
                : productPickerActive
                  ? productOptions.length === 0
                    ? (
                        <Box
                          sx={{
                            px: 1.5,
                            py: 1.5,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          {catalogLoading && catalogEnabled ? <CircularProgress size={22} /> : null}
                          <Typography variant="body2" color="text.secondary" textAlign="center">
                            {!projectId
                              ? "Sem projeto na solicitação: só é possível listar produtos já vinculados ao protocolo. Defina o projeto para buscar o catálogo completo."
                              : catalogLoading && catalogEnabled
                                ? "Carregando catálogo…"
                                : "Nenhum produto corresponde à busca."}
                          </Typography>
                        </Box>
                      )
                    : productOptions.map((p, idx) => {
                        const imgOk = Boolean(p.coverImageUrl && isWhatsappAcceptedImageUrl(p.coverImageUrl));
                        return (
                          <Box
                            key={p.rowKey}
                            display="flex"
                            alignItems="center"
                            gap={0.5}
                            sx={{
                              px: 0.5,
                              py: 0.25,
                              borderLeft: p.inCart ? 3 : 0,
                              borderColor: p.inCart ? "primary.main" : "transparent",
                              bgcolor: idx === productHighlightIndex ? "action.selected" : "transparent",
                              "&:hover": { bgcolor: "action.hover" },
                            }}
                          >
                            {p.inCart ? (
                              <Chip
                                size="small"
                                label="Protocolo"
                                color="primary"
                                variant="outlined"
                                sx={{ height: 22, flexShrink: 0, "& .MuiChip-label": { px: 0.75 } }}
                              />
                            ) : null}
                            <Box
                              component="div"
                              flex={1}
                              minWidth={0}
                              onClick={() => handleSelectProductText(p)}
                              onMouseDown={(e) => e.preventDefault()}
                              sx={{ cursor: "pointer", py: 0.5, pl: p.inCart ? 0 : 0.5 }}
                            >
                              <Typography variant="body2" noWrap>
                                {p.nome}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" component="span">
                                (
                                {formatCartCurrency(p.valor)} · {p.quantidade} un.
                                {p.sku ? ` · SKU ${p.sku}` : ""}
                                {!p.inCart ? " · Catálogo" : ""})
                              </Typography>
                            </Box>
                            {!p.inCart && p.productId ? (
                              <Tooltip title="Incluir no protocolo (carrinho)">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    disabled={cartSaving}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleAddProductToConversation(p);
                                    }}
                                    onMouseDown={(e) => e.preventDefault()}
                                  >
                                    {cartSaving ? (
                                      <CircularProgress size={16} color="inherit" />
                                    ) : (
                                      <AddShoppingCart fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            ) : null}
                            {p.coverImageUrl ? (
                              <Tooltip
                                title={
                                  imgOk
                                    ? "Carregar foto no envio (Shift+Enter na lista)"
                                    : "Imagem em formato não aceito pelo WhatsApp (JPEG/PNG). Atualize no cadastro do produto."
                                }
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    color={imgOk ? "secondary" : "error"}
                                    disabled={!imgOk}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSelectProductImage(p);
                                    }}
                                    onMouseDown={(e) => e.preventDefault()}
                                  >
                                    <ImageIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            ) : null}
                          </Box>
                        );
                      })
                  : null}
            </Box>,
            document.body
          )}
        <Snackbar
          open={videoReuseSnackbarOpen}
          autoHideDuration={6000}
          onClose={() => setVideoReuseSnackbarOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            severity="info"
            onClose={() => setVideoReuseSnackbarOpen(false)}
            sx={{ width: "100%" }}
          >
            {t(
              "video.reusedRoomSnackbar",
              "Já existe uma chamada ativa — o mesmo link será reenviado"
            )}
          </Alert>
        </Snackbar>
        <Snackbar
          open={videoSending}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            severity="info"
            variant="filled"
            icon={<CircularProgress size={18} color="inherit" thickness={5} />}
            sx={{ width: "100%" }}
          >
            {t("video.preparingInvite", "Preparando convite de vídeo…")}
          </Alert>
        </Snackbar>
        <Snackbar
          open={phoneBlockSnackbarOpen}
          autoHideDuration={6000}
          onClose={() => setPhoneBlockSnackbarOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setPhoneBlockSnackbarOpen(false)}
            severity="warning"
            variant="filled"
            sx={{ width: "100%" }}
          >
            {t("input.phoneRequiredSnackbar")}
          </Alert>
        </Snackbar>
        <Snackbar
          open={composeRuleSnackbarOpen}
          autoHideDuration={7000}
          onClose={() => {
            setComposeRuleSnackbarOpen(false);
            setComposeRuleKey(null);
          }}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => {
              setComposeRuleSnackbarOpen(false);
              setComposeRuleKey(null);
            }}
            severity="warning"
            variant="filled"
            sx={{ width: "100%" }}
          >
            {composeRuleKey === "caption"
              ? t("input.composeMediaNeedsCaption")
              : composeRuleKey === "metaWindow"
                ? t("input.composeMetaWindowExpired", {
                    channel: getMetaChannelLabel(channelType),
                  })
                : t("input.composeRequiresTemplate")}
          </Alert>
        </Snackbar>
        <Dialog
          open={mentionConfirmOpen}
          onClose={() => {
            setMentionConfirmOpen(false);
            setMentionConfirmKind("colleague");
          }}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>
            {mentionConfirmKind === "whatsappFirst"
              ? t("input.mentionWhatsappFirstTitle")
              : t("input.mentionConfirmTitle")}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              {mentionConfirmKind === "whatsappFirst"
                ? t("input.mentionWhatsappFirstBody")
                : t("input.mentionConfirmBody")}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: "wrap" }}>
            <Button
              onClick={() => {
                setMentionConfirmOpen(false);
                setMentionConfirmKind("colleague");
              }}
            >
              {t("input.mentionConfirmCancel")}
            </Button>
            {mentionConfirmKind !== "whatsappFirst" ? (
              <Button variant="outlined" onClick={handleMentionSaveAsInternal}>
                {t("input.mentionConfirmInternal")}
              </Button>
            ) : null}
            <Button variant="contained" onClick={handleMentionSendToClient}>
              {mentionConfirmKind === "whatsappFirst"
                ? t("input.mentionWhatsappFirstConfirm")
                : t("input.mentionConfirmClient")}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={Boolean(videoGuide)}
          onClose={calendarConnecting ? undefined : () => setVideoGuide(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>{videoGuide?.title || t("video.errorTitle")}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              {videoGuide?.message || t("video.errorGeneric")}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: "wrap" }}>
            <Button onClick={() => setVideoGuide(null)} disabled={calendarConnecting}>
              {t("video.close")}
            </Button>
            {videoGuide?.kind === "meet_calendar" ? (
              <>
                <Button
                  component={RouterLink}
                  to="/profile"
                  onClick={() => setVideoGuide(null)}
                  disabled={calendarConnecting}
                >
                  {t("video.openProfile")}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleConnectGoogleCalendar}
                  disabled={calendarConnecting}
                  startIcon={calendarConnecting ? <CircularProgress size={14} color="inherit" /> : null}
                >
                  {calendarConnecting ? t("video.connectingCalendar") : t("video.connectCalendar")}
                </Button>
              </>
            ) : null}
            {videoGuide?.kind === "meet_local_user" ? (
              <Button
                component={RouterLink}
                to="/profile"
                variant="contained"
                onClick={() => setVideoGuide(null)}
              >
                {t("video.openProfile")}
              </Button>
            ) : null}
            {videoGuide?.kind === "channel_missing" ? (
              <Button
                variant="contained"
                component={RouterLink}
                to="/settings/channels"
                onClick={() => setVideoGuide(null)}
              >
                {t("video.openChannels")}
              </Button>
            ) : null}
            {videoGuide?.kind === "schema_missing" ? (
              <Button
                variant="contained"
                onClick={() => {
                  setVideoGuide(null);
                  handleStartXproVideo({ requireCamera: lastRequireCameraRef.current });
                }}
              >
                {t("video.retry")}
              </Button>
            ) : null}
          </DialogActions>
        </Dialog>
        <StartVideoCallDialog
          open={startVideoDialogOpen}
          busy={videoSending}
          context="support"
          canSendInvite={
            channelSupportsWhatsAppInviteButton || channelAllowsInviteComposeText
          }
          activeRoomExists={Boolean(
            activeVideoRoom?.joinUrl || activeVideoRoom?.agentJoinUrl || activeVideoRoom?.id
          )}
          onClose={() => setStartVideoDialogOpen(false)}
          onConfirm={({ requireCamera, recordingEnabled }) =>
            handleStartXproVideo({ requireCamera, recordingEnabled })
          }
        />
        <Dialog
          open={locationConfirmOpen}
          onClose={handleCancelLocationConfirm}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>{t("location.confirmTitle")}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {t("location.approximateHint")}
            </Typography>
            {locationDraft &&
              (() => {
                const preview = buildStaticMapPreviewUrl(
                  locationDraft.latitude,
                  locationDraft.longitude,
                  { width: 560, height: 220 }
                );
                if (!preview) return null;
                if (locationConfirmMapFailed) {
                  return (
                    <Box
                      sx={{
                        width: "100%",
                        height: 160,
                        borderRadius: 1,
                        mb: 1,
                        bgcolor: "action.hover",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <PlaceRounded color="error" sx={{ fontSize: 48, opacity: 0.9 }} />
                    </Box>
                  );
                }
                return (
                  <Box
                    component="img"
                    src={preview}
                    alt=""
                    referrerPolicy="no-referrer"
                    onError={() => setLocationConfirmMapFailed(true)}
                    sx={{
                      width: "100%",
                      height: 160,
                      objectFit: "cover",
                      borderRadius: 1,
                      display: "block",
                      mb: 1,
                      bgcolor: "action.hover",
                    }}
                  />
                );
              })()}
            {locationDraft && (
              <Typography variant="caption" color="text.secondary" component="div">
                {Number(locationDraft.latitude).toFixed(5)},{" "}
                {Number(locationDraft.longitude).toFixed(5)}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelLocationConfirm}>{t("location.confirmCancel")}</Button>
            <Button variant="contained" onClick={handleConfirmLocationSend}>
              {t("location.confirmSend")}
            </Button>
          </DialogActions>
        </Dialog>
        <ShareContactDialog
          open={shareContactOpen}
          onClose={() => setShareContactOpen(false)}
          onConfirm={handleConfirmShareContact}
          projectId={selectedConversation?.projectId}
        />
      </Box>
    </FileDropHandler>
  );
};

export default MessageInput;
